import React, { useEffect, useRef, useState } from 'react';
import { createChart, UTCTimestamp, LineSeries, CandlestickSeries, ColorType } from 'lightweight-charts';
import { calculateAllEMAs, calculateBollingerBands, OHLCData, calculateMACD, calculateRSI } from '../composables/chartUtils';
import LineIcon from '../assets/icons/LineIcon';
import SquareIcon from '../assets/icons/SquareIcon';
import { findVolumeProfileSR } from '../composables/supportResistanceUtils';
import { findLowHighSR } from '../composables/supportResistanceUtils';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'react-hot-toast';
import { set } from 'date-fns';

interface StockChartProps {
  data: OHLCData[];
  historicalData?: OHLCData[];
  chartType?: 'candlestick' | 'heikin-ashi';
  showBollinger?: boolean;
  priceLineType?: 'none' | 'freestyle' | 'support-resistance';
  lowerIndicatorData?: { time: number; value: number }[];
  zoomScope?: string;
  showOverlays?: boolean;
  showDashed?: boolean;
  onSupportResistanceUpdate?: (
    data: { tag: string; price: number | null } | { tag: string; price: number | null }[]
  ) => void;
  onClearAllSR?: () => void;
  avgBuyPrice?: number | null;
  showSLine?: boolean;
  showRLine?: boolean;
  showTrendline?: boolean;
  frequency?: 'minute' | 'hour' | 'day' | 'week' | 'month'; // <-- add this line
  onIndicatorsReady?: (data: any) => void;
  symbol?: string; // <-- add this line
  setShowSLine?: (value: boolean) => void; // Add setShowSLine
  setShowRLine?: (value: boolean) => void; // Add setShowRLine
  setShowTrendline?: (value: boolean) => void; // Add setShowTrendline
  mode: string // รับ mode จาก props
  setModeLoss: (mode: string) => void; // เพิ่ม prop สำหรับ setModeLoss
  onPriceClick?: (price: number) => void; // Add onPriceClick prop
  onDirtyChange?: (dirty: boolean) => void;
  isSRLocked?: boolean;
  setChartRef?: (chart: any) => void;               // 🌟 เพิ่มบรรทัดนี้
  onTimeRangeChange?: (range: any) => void;      // 🌟 เพิ่มบรรทัดนี้
}

// Heikin Ashi calculation
function calculateHeikinAshi(data: OHLCData[]) {
  if (!data.length) return [];
  const haData = [];
  let prevHaClose = (data[0].o + data[0].h + data[0].l + data[0].c) / 4;
  let prevHaOpen = (data[0].o + data[0].c) / 2;
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    const haClose = (d.o + d.h + d.l + d.c) / 4;
    const haOpen = i === 0 ? prevHaOpen : (prevHaOpen + prevHaClose) / 2;
    const haHigh = Math.max(d.h, haOpen, haClose);
    const haLow = Math.min(d.l, haOpen, haClose);
    haData.push({
      time: Math.floor(d.t / 1000) as UTCTimestamp,
      open: haOpen,
      high: haHigh,
      low: haLow,
      close: haClose,
    });
    prevHaOpen = haOpen;
    prevHaClose = haClose;
  }
  return haData;
}
type DrawingLine = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startTime?: any;
  startPrice?: any;
  endTime?: any;
  endPrice?: any;
};

type DrawingArea = {
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  startTime?: number;
  startPrice?: number;
  endTime?: number;
  endPrice?: number;
  tag?: string;
};

const reorderSRLines = (allLines: any[], currentPrice: number) => {
  // 1. ล้างสถานะเดิม แล้วตัดสินประเภท (Role) ใหม่แบบ Dynamic 100% เทียบกับราคาปัจจุบัน
  const updatedTypeLines = allLines.map(line => {
    // กำหนดประเภทจริงหน้างาน
    const isActuallySupport = line.price <= currentPrice;
    return {
      ...line,
      type: isActuallySupport ? 'support' : 'resistance'
    };
  });

  // 2. แยกสาย Support (ต่ำกว่าหรือเท่ากับราคาปัจจุบัน) และเรียงลำดับจาก "แพงไปถูก" (S1 แพงสุด)
  const supports = updatedTypeLines
    .filter((l) => l.type === 'support')
    .sort((a, b) => b.price - a.price);

  // 3. แยกสาย Resistance (สูงกว่าราคาปัจจุบัน) และเรียงลำดับจาก "ถูกไปแพง" (R1 ถูกสุด)
  const resistances = updatedTypeLines
    .filter((l) => l.type === 'resistance')
    .sort((a, b) => a.price - b.price);

  // 4. 🔥 จุดตาย: บังคับจ่ายชื่อ Tag ใหม่ (S1, S2 / R1, R2) เพื่อแก้ป้ายชื่อหลอน
  const newSupports = supports.map((l, idx) => ({
    ...l,
    tag: `S${idx + 1}`,
    color: "#fde047" // บังคับสีเหลือง
  }));

  const newResistances = resistances.map((l, idx) => ({
    ...l,
    tag: `R${idx + 1}`,
    color: "#a78bfa" // บังคับสีม่วง
  }));

  // คืนค่า Object ครบชุดให้หน้าบ้านนำไปใช้งานต่อ
  const finalLines = [...newSupports, ...newResistances];

  return {
    lines: finalLines,
    supportTags: newSupports.map(l => l.tag),
    resistanceTags: newResistances.map(l => l.tag)
  };
};
const StockChart = ({
  data,
  historicalData, // Use historicalData for EMA calculations
  chartType = 'candlestick',
  showBollinger = false,
  priceLineType = 'none',
  lowerIndicatorData = [],
  zoomScope = '6M',
  showOverlays = false,
  showDashed = false,
  onSupportResistanceUpdate, // Use the callback
  onClearAllSR, // <-- add this
  avgBuyPrice,
  showSLine = true,
  showRLine = true,
  showTrendline = true,
  onIndicatorsReady, // <-- Destructure here
  symbol, // <-- Destructure here
  frequency, // 🌟 ใช้เช็คว่า historicalData (รายวันเสมอ) ตรงกับ resolution ของ data ที่กำลังแสดงหรือไม่
  setShowSLine,
  setShowRLine,
  setShowTrendline,
  mode = 'default', // รับ mode จาก props
  setModeLoss,
  onPriceClick,
  onDirtyChange,
  isSRLocked,
  setChartRef,              // 🌟 เพิ่มบรรทัดนี้
  onTimeRangeChange      // 🌟 เพิ่มบรรทัดนี้
}: StockChartProps) => {
  const lowerRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);
  const [activeTool, setActiveTool] = useState<'none' | 'line' | 'sr-line' | 'area' | 'drag'>('none');
  const [lines, setLines] = useState<any[]>([]); // Store drawn lines
  const [areas, setAreas] = useState<any[]>([]); // Store drawn areas
  const [draggingLine, setDraggingLine] = useState<{ tag: string; type: 'support' | 'resistance' } | null>(null);
  const [supportTags, setSupportTags] = useState<string[]>([]); // Store support tags
  const [resistanceTags, setResistanceTags] = useState<string[]>([]); // Store resistance tags
  const [drawingLine, setDrawingLine] = useState<null | DrawingLine>(null);
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [drawingArea, setDrawingArea] = useState<null | DrawingArea>(null);
  const [isDrawingArea, setIsDrawingArea] = useState(false);
  const resistanceCounterRef = useRef(1);
  const supportCounterRef = useRef(1);
  const chartRef = useRef<HTMLDivElement>(null);
  // ความสูงชาร์ตหลักคงที่ ห้ามวัดจาก DOM เพราะ chartRef สูงตามชาร์ตเอง (วนอ้างอิงตัวเองจนหด)
  const MAIN_CHART_HEIGHT = 300;
  const [chartSize, setChartSize] = useState({ width: 900, height: MAIN_CHART_HEIGHT });
  const [history, setHistory] = useState<any[]>([]);
  const [hoveredTag, setHoveredTag] = useState<string | null>(null);
  const [hoveredLineIdx, setHoveredLineIdx] = useState<number | null>(null);
  const visibleRangeSetRef = useRef(false);
  // 🌟 เก็บ "ลายเซ็น" ของชุดข้อมูลล่าสุด (symbol/frequency เปลี่ยน = ชุดข้อมูลใหม่จริง)
  // ใช้แยกแยะ: recreate เพราะข้อมูลเปลี่ยนจริง (ควร reset zoom) กับ recreate เพราะ toggle
  // ปุ่ม/ธีมอื่นๆ (ควร "จำ" ตำแหน่ง pan/zoom เดิมไว้ ไม่ใช่กระโดดกลับไป range เริ่มต้น)
  const lastDatasetKeyRef = useRef<string | null>(null);
  const [visibleRangeVersion, setVisibleRangeVersion] = useState(0);
  // 🌟 เพิ่มค่าทุกครั้งที่สร้างชาร์ตใหม่ ใช้เป็นสัญญาณให้วาดเส้น S/R ลงชาร์ตตัวใหม่
  const [chartVersion, setChartVersion] = useState(0);
  const lineSeriesMap = useRef<Map<string, any>>(new Map());
  const [trendLines, setTrendLines] = useState<any[]>([]);
  const [srLines, setSrLines] = useState<{ tag: string; price: number; type: 'support' | 'resistance'; startY: number; endY: number }[]>([]);
  const [draggingSRTag, setDraggingSRTag] = useState<{ tag: string; type: 'support' | 'resistance' } | null>(null);
  const lastSentSymbolRef = useRef<string | undefined>(undefined);
  const trendLineSeriesMap = useRef<Map<string, any>>(new Map());
  const [selectedTrendLineTag, setSelectedTrendLineTag] = useState<string | null>(null);
  const [selectedAreaTag, setSelectedAreaTag] = useState<string | null>(null);
  const lastTapRef = useRef<number>(0); // สำหรับเก็บเวลาที่แตะครั้งล่าสุด
  const { theme, toggleTheme } = useTheme(); // Use the theme toggle function
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // ฟังก์ชันสำหรับเช็คความต่าง
  const checkIfDirty = (currentLines: any[]) => {
    const storageKey = `chartLines_${symbol}`;
    const saved = localStorage.getItem(storageKey);
    if (!saved) return currentLines.length > 0; // ถ้าไม่มีของเก่าเลย แต่ตอนนี้มีเส้น = Dirty

    const parsed = JSON.parse(saved);
    // เทียบ JSON string ของเฉพาะส่วนราคาและ Tag
    return JSON.stringify(parsed.lines) !== JSON.stringify(currentLines);
  };
  // console.log(theme)
  useEffect(() => {
    // เคลียร์ทุกอย่างเมื่อ symbol เปลี่ยน
    // setLines([]);
    // setAreas([]);
    // setSupportTags([]);
    // setResistanceTags([]);
    // setTrendLines([]);
    // setSrLines([]);
    setDrawingLine(null);
    setDrawingArea(null);
    setIsDrawingLine(false);
    setIsDrawingArea(false);
    setSelectedTrendLineTag(null);

    // ลบ series ทั้งหมดใน lightweight-charts
    if (chartInstance.current) {
      // ลบ S/R
      lineSeriesMap.current.forEach(series => {
        chartInstance.current.removeSeries(series);
      });
      lineSeriesMap.current.clear();

      // ลบ trendline
      if (chartInstance.current.freestyleTrendSeries) {
        chartInstance.current.freestyleTrendSeries.forEach((series: any) => {
          chartInstance.current.removeSeries(series);
        });
        chartInstance.current.freestyleTrendSeries = [];
      }
      trendLineSeriesMap.current.clear();

      // ลบ area
      if (chartInstance.current.areaSeries) {
        chartInstance.current.areaSeries.forEach((series: any) => {
          chartInstance.current.removeSeries(series);
        });
        chartInstance.current.areaSeries = [];
      }
    }

    // เคลียร์ localStorage ถ้าต้องการ
    localStorage.removeItem('areas');
    localStorage.removeItem('trendLines');
    localStorage.removeItem('chartLines');
  }, [symbol]);
  // Save areas to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('areas', JSON.stringify(areas));
  }, [areas]);

  // Load areas from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('areas');
    if (saved) {
      try {
        setAreas(JSON.parse(saved));
      } catch (e) { }
    }
  }, []);

  // Restore areas from localStorage when showTrendline toggled on
  useEffect(() => {
    if (showTrendline) {
      const saved = localStorage.getItem('areas');
      if (saved) {
        try {
          setAreas(JSON.parse(saved));
        } catch (e) { }
      }
    }
    // ถ้าอยากให้ซ่อน area ตอน showTrendline=false ให้ setAreas([]) ได้
  }, [showTrendline]);


  useEffect(() => {
    if (!chartInstance.current || priceLineType !== 'freestyle') return;

    // ลบ series เดิมก่อน
    if (chartInstance.current.areaSeries) {
      chartInstance.current.areaSeries.forEach((series: any) => {
        chartInstance.current.removeSeries(series);
      });
    }
    chartInstance.current.areaSeries = [];

    if (showTrendline) {
      areas.forEach((area) => {
        if (
          area.startTime === undefined ||
          area.startPrice === undefined ||
          area.endTime === undefined ||
          area.endPrice === undefined
        ) return;
        const step = (area.endPrice - area.startPrice) / 50; // 50 lines
        for (let i = 0; i <= 50; i++) {
          const price = area.startPrice + step * i;
          const fillSeries = chartInstance.current.addSeries(LineSeries, {
            color: '#60a5fa',
            lineWidth: 0.4,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false, // << ปิดจุด marker
          });
          fillSeries.setData([
            { time: area.startTime, value: price },
            { time: area.endTime, value: price },
          ]);
          chartInstance.current.areaSeries.push(fillSeries);
        }
        // Top edge (แนวนอน)
        const topSeries = chartInstance.current.addSeries(LineSeries, {
          color: '#60a5fa',
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false, // << ปิดจุด marker
        });
        topSeries.setData([
          { time: area.startTime, value: area.startPrice },
          { time: area.endTime, value: area.startPrice },
        ]);
        chartInstance.current.areaSeries.push(topSeries);

        // Bottom edge (แนวนอน)
        const bottomSeries = chartInstance.current.addSeries(LineSeries, {
          color: '#60a5fa',
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false, // << ปิดจุด marker
        });
        bottomSeries.setData([
          { time: area.startTime, value: area.endPrice },
          { time: area.endTime, value: area.endPrice },
        ]);
        chartInstance.current.areaSeries.push(bottomSeries);

        // Left edge (แนวตั้ง) -- ต้องใช้ช่วงเวลา
        const leftSeries = chartInstance.current.addSeries(LineSeries, {
          color: '#60a5fa',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        // leftSeries.setData([
        //   { time: area.startTime, value: area.startPrice },
        //   { time: area.startTime - 0.000001, value: area.endPrice - 0.000001 },
        // ]);
        chartInstance.current.areaSeries.push(leftSeries);

        // Right edge (แนวตั้ง) -- ต้องใช้ช่วงเวลา
        const rightSeries = chartInstance.current.addSeries(LineSeries, {
          color: '#60a5fa',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        // rightSeries.setData([
        //   { time: area.endTime + 0.000001, value: area.startPrice + 0.000001 },
        //   { time: area.endTime, value: area.endPrice },
        // ]);
        chartInstance.current.areaSeries.push(rightSeries);
      });

    }
  }, [areas, showTrendline, priceLineType, lines, supportTags, resistanceTags, chartInstance.current]);







  useEffect(() => {
    // ลบ series ที่ไม่มีใน trendLines
    if (!chartInstance.current) return;
    const tags = new Set(trendLines.map(l => l.tag));
    Array.from(trendLineSeriesMap.current.keys()).forEach(tag => {
      if (!tags.has(tag)) {
        const s = trendLineSeriesMap.current.get(tag);
        if (s) {
          chartInstance.current.removeSeries(s);
          trendLineSeriesMap.current.delete(tag);
        }
      }
    });
  }, [trendLines]);


  // Sync freestyle trendLines to chart
  useEffect(() => {
    localStorage.setItem('trendLines', JSON.stringify(trendLines));
  }, [trendLines]);

  // Load trendLines from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('trendLines');
    if (saved) {
      try {
        setTrendLines(JSON.parse(saved));
      } catch (e) { }
    }
  }, []);

  useEffect(() => {
    if (!chartInstance.current || priceLineType !== 'freestyle') return;

    // สร้าง array เก็บ series ถ้ายังไม่มี
    if (!chartInstance.current.freestyleTrendSeries) {
      chartInstance.current.freestyleTrendSeries = [];
    }
    chartInstance.current.freestyleTrendSeries = [];

    // ถ้า trendLines เปลี่ยนจำนวน ให้สร้างใหม่
    if (chartInstance.current.freestyleTrendSeries) {
      // ลบ series เดิม
      chartInstance.current.freestyleTrendSeries.forEach((series: any) => {
        chartInstance.current.removeSeries(series);
      });
      chartInstance.current.freestyleTrendSeries = [];

      // สร้างใหม่ตาม trendLines
      trendLineSeriesMap.current.clear();
      // ถ้า showTrendline=false ให้ลบ series ทั้งหมด
      if (!showTrendline) {
        if (chartInstance.current.freestyleTrendSeries) {
          chartInstance.current.freestyleTrendSeries.forEach((series: any) => {
            chartInstance.current.removeSeries(series);
          });
          chartInstance.current.freestyleTrendSeries = [];
        }
        trendLineSeriesMap.current.clear();
        return;
      }

      trendLines.forEach((line) => {
        if (!line.startTime || !line.startPrice || !line.endTime || !line.endPrice) return;
        const series = chartInstance.current.addSeries(LineSeries, {
          color: '#38bdf8',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        series.setData([
          { time: line.startTime, value: line.startPrice },
          { time: line.endTime, value: line.endPrice },
        ]);
        series.applyOptions({ visible: !!showTrendline });
        chartInstance.current.freestyleTrendSeries.push(series);

        trendLineSeriesMap.current.set(line.tag, series); // map ด้วย tag
      });
    } else {
      // update visibility เฉยๆ
      chartInstance.current.freestyleTrendSeries.forEach((series: any) => {
        series.applyOptions({ visible: !!showTrendline });
      });
    }
  }, [trendLines, showTrendline, priceLineType]);

  useEffect(() => {
    localStorage.setItem('trendLines', JSON.stringify(trendLines));
  }, [trendLines]);
  useEffect(() => {
    if (showTrendline) {
      const saved = localStorage.getItem('trendLines');
      if (saved) {
        try {
          setTrendLines(JSON.parse(saved));
        } catch (e) { }
      }
    }
    // ถ้าไม่โชว์ trendline สามารถ setTrendLines([]) เพื่อซ่อนได้ (หรือใช้ applyOptions ตามเดิม)
  }, [showTrendline, resistanceTags, chartInstance.current]);

  function handleDeleteTrendLine(tag: string) {
    setTrendLines(prev => {
      const newTrendLines = prev.filter(l => l.tag !== tag);
      return newTrendLines;
    });
    // Trigger อัพเดตการโชว์ trendline (ถ้าใช้ state showTrendline)
    if (setShowTrendline) {
      setShowTrendline(false);
      setTimeout(() => setShowTrendline(true), 10); // รีเฟรช UI
    }
  }


  useEffect(() => {
    if (!chartInstance.current || !chartInstance.current.mainSeries || srLines.length === 0) return;
    setSrLines(prev =>
      prev.map(line => {
        const y = chartInstance.current.mainSeries.priceToCoordinate(line.price);
        return { ...line, startY: y, endY: y };
      })
    );
  }, [chartSize, zoomScope, data, srLines.length, visibleRangeVersion]);


  const handleChartClick = (event: React.MouseEvent) => {
    if (!chartRef.current || !chartInstance.current) return;

    const rect = chartRef.current.getBoundingClientRect();
    const y = event.clientY - rect.top; // Get Y-coordinate relative to the chart
    const price = chartInstance.current.mainSeries.coordinateToPrice(y); // Convert Y-coordinate to price

    if (price > currentPrice && mode === 'damage-view') {
      toast.error('กรุณาเลือกราคาที่ต่ำกว่าปัจจุบัน'); // Show error toast
      return;
    }
    // console.log('Clicked price:', price); // Debugging log

    if (onPriceClick && price && mode === 'damage-view') {
      onPriceClick(price.toFixed(2)); // Send the price to the parent component
      setModeLoss('default'); // เปลี่ยนโหมดเป็น "default"
    }
  };



  const handleSRTagMouseDown = (tag: string, type: 'support' | 'resistance') => {
    if (isSRLocked) return;
    setDraggingSRTag({ tag, type });

  };
  const handleSRTagMouseMove = (event: React.MouseEvent) => {
    if (!draggingSRTag || !chartRef.current) return;
    const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
    const y = event.clientY - rect.top;

    if (!chartInstance.current || !chartInstance.current.mainSeries) return;
    const mainSeries = chartInstance.current.mainSeries;
    const price = mainSeries.coordinateToPrice(y);

    setSrLines((prev) =>
      prev.map((line) =>
        line.tag === draggingSRTag.tag
          ? { ...line, startY: y, endY: y, price }
          : line
      )
    );
    addOrUpdateSRLine(draggingSRTag.tag, price, draggingSRTag.type);

    if (onSupportResistanceUpdate) {
      onSupportResistanceUpdate({ tag: draggingSRTag.tag, price });
    }
  };

  const handleSRTagMouseUp = () => {
    if (!draggingSRTag) {
      setDraggingSRTag(null);
      return;
    }

    // ✅ เปลี่ยนมาใช้ Pure Function reorderSRLines ที่เราสร้างไว้ เพื่อให้ฉลาดสลับฝั่งตามราคาปัจจุบัน
    setSrLines((prevLines) => {
      const result = reorderSRLines(prevLines, currentPrice);
      const finalReorderedLines = result.lines;

      // ล้าง Series เก่าบนชาร์ต
      lineSeriesMap.current.forEach((series) => {
        if (chartInstance.current) {
          try { chartInstance.current.removeSeries(series); } catch (e) { }
        }
      });
      lineSeriesMap.current.clear();

      // วาดเส้นลง Lightweight Charts ใหม่
      finalReorderedLines.forEach((line) => {
        addOrUpdateSRLine(line.tag, line.price, line.type as any);
      });

      // ส่งข้อมูลกลับไปที่ Parent (HomePage)
      if (onSupportResistanceUpdate) {
        onSupportResistanceUpdate(
          finalReorderedLines.map((l) => ({ tag: l.tag, price: l.price }))
        );
      }

      return finalReorderedLines as any;
    });

    setDraggingSRTag(null);
  };

  function addOrUpdateSRLine(tag: string, price: number, type: 'support' | 'resistance') {
    const oldSeries = lineSeriesMap.current.get(tag);
    // console.log('oldSeries:', chartInstance.current);
    if (oldSeries && chartInstance.current) {
      chartInstance.current.removeSeries(oldSeries);
      lineSeriesMap.current.delete(tag); // Remove reference after removing
    }
    // Only add new series if price is defined and not NaN
    if (chartInstance.current && typeof price === 'number' && !isNaN(price)) {

      const isSupport = price <= currentPrice;
      const color = isSupport ? '#fde047' : '#a78bfa';
      const newSeries = chartInstance.current.addSeries(LineSeries, {
        color,
        lineWidth: 2,
        title: showOverlays ? tag : ''
      });
      newSeries.setData([
        { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: price },
        { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: price },
      ]);
      lineSeriesMap.current.set(tag, newSeries);
    }

  }


  // Responsive chart size using ResizeObserver
  useEffect(() => {
    const handleResize = () => {
      if (!chartRef.current) return;
      const rect = chartRef.current.getBoundingClientRect();
      // 🌟 อ่านแค่ความกว้าง: ถ้าอ่านความสูงด้วย chartRef จะสูงเท่าที่ชาร์ตสั่งไว้เอง
      // พอ layout บีบชาร์ตเตี้ยลงครั้งเดียว resize() จะล็อกค่านั้นไว้และหดต่อจนหายไป
      const width = Math.floor(rect.width);
      const height = MAIN_CHART_HEIGHT;
      if (!width) return;

      setChartSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));

      // 🌟 จุดตาย: เดิมอัปเดตแค่ state chartSize แต่ chart instance ที่สร้างไปแล้วไม่เคย
      // ถูกสั่ง resize ใหม่ (chartSize ไม่ได้อยู่ใน dependency ของ effect ที่สร้างชาร์ต)
      // เลยค้างขนาดเดิมตอนลาก resize คอลัมน์ ต้อง resize() ตรงๆ ที่ instance ทันที
      // เพราะ resize() ไม่ทำลาย zoom/pan/เส้นที่วาดไว้เหมือนการสร้างชาร์ตใหม่ทั้งก้อน
      if (chartInstance.current) {
        chartInstance.current.resize(width, height);
      }
    };

    let observer: ResizeObserver | null = null;
    if (chartRef.current) {
      observer = new ResizeObserver(handleResize);
      observer.observe(chartRef.current);
      handleResize();
    }
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (observer && chartRef.current) observer.unobserve(chartRef.current);
    };
  }, []);

  // Helper to get filtered data by zoomScope
  function filterDataByRange(data: OHLCData[], zoomScope: string): OHLCData[] {
    if (!data.length) return [];
    const lastDate = new Date(data[data.length - 1].t);
    let fromDate = new Date(lastDate);
    switch (zoomScope) {
      case '1D':
        fromDate.setDate(lastDate.getDate() - 1);
        break;
      case '5D':
        fromDate.setDate(lastDate.getDate() - 5);
        break;
      case '1M':
        fromDate.setMonth(lastDate.getMonth() - 1);
        break;
      case '6M':
        fromDate.setMonth(lastDate.getMonth() - 6);
        break;
      case 'YTD':
        fromDate = new Date(lastDate.getFullYear(), 0, 1);
        break;
      case '1Y':
        fromDate.setFullYear(lastDate.getFullYear() - 1);
        break;
      case '5Y':
        fromDate.setFullYear(lastDate.getFullYear() - 5);
        break;
      default:
        fromDate.setMonth(lastDate.getMonth() - 6);
    }
    return data.filter(d => new Date(d.t) >= fromDate);
  }
  useEffect(() => {
    if (!chartRef.current || !data.length) return;

    // 🌟 จำตำแหน่ง pan/zoom (logical range) ของชาร์ตเดิมไว้ก่อนทำลายทิ้ง
    // เพื่อคืนค่ากลับหลังสร้างชาร์ตใหม่ ถ้าข้อมูลชุดเดิม (แค่ toggle ปุ่ม/ธีม/ชนิดกราฟ)
    let preservedLogicalRange: any = null;
    if (chartInstance.current) {
      try {
        preservedLogicalRange = chartInstance.current.timeScale().getVisibleLogicalRange();
      } catch (e) { }
    }

    // Clean up previous chart instance
    if (chartInstance.current) {
      chartInstance.current.remove();
      chartInstance.current = null;
    }

    // Remove all S/R series from previous chart
    lineSeriesMap.current.forEach(series => {
      try {
        if (series && typeof series.remove === 'function') series.remove();
        else if (chartInstance.current) chartInstance.current.removeSeries(series);
      } catch (e) { }
    });
    lineSeriesMap.current.clear();

    const chart = createChart(chartRef.current, {
      width: chartSize.width,
      height: chartSize.height,
      layout: {
        background: { type: ColorType.Solid, color: theme === 'dark' ? '#18181b' : '#ffffff' },
        textColor: theme === 'dark' ? '#e5e7eb' : '#000000', // Adjust text color for light theme
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#27272a' : '#e0e0e0' }, // Adjust grid line color
        horzLines: { color: theme === 'dark' ? '#27272a' : '#e0e0e0' },
      },
      crosshair: {
        vertLine: { color: '#facc15', width: 1 },
        horzLine: { color: '#facc15', width: 1 },
        mode: 0,
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: {
        mouseWheel: true,     // เปิดให้เลื่อนด้วย scroll mouse
        pressedMouseMove: true, // กดเม้าส์ลากเพื่อเลื่อน
        horzTouchDrag: true,
        vertTouchDrag: true,    // เปิดให้เลื่อนแนวตั้งได้ด้วย (ถ้าใช้ touch)
      },
      handleScale: {
        axisPressedMouseMove: true, // กดเม้าส์ลากเพื่อซูมแนวนอนและแนวตั้ง
        axisDoubleClickReset: true,
        mouseWheel: true,
        pinch: true,
      },
    });
    chartInstance.current = chart;
    // 🌟 แจ้งว่ามีชาร์ตตัวใหม่ เพื่อให้ effect sync เส้น S/R วาดเส้นหนาลงชาร์ตใหม่นี้
    setChartVersion(v => v + 1);
    // 🌟 ส่งอินสแตนซ์ให้ Parent
    if (setChartRef) setChartRef(chart);

    // 🌟 ดักจับการเลื่อนกราฟ X
    const logicalRangeHandler = (logicalRange: any) => {
      if (onTimeRangeChange && logicalRange) {
        onTimeRangeChange(logicalRange);
      }
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(logicalRangeHandler);
    let mainSeries;
    if (chartType === 'candlestick') {
      mainSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
        priceLineVisible: showDashed ? true : false,
      });
      mainSeries.setData(
        data.map((item) => ({
          time: Math.floor(item.t / 1000) as UTCTimestamp,
          open: item.o,
          high: item.h,
          low: item.l,
          close: item.c,
        }))
      );
    } else if (chartType === 'heikin-ashi') {
      mainSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#22c55e',
        downColor: '#f87171',
        borderUpColor: '#22c55e',
        borderDownColor: '#f87171',
        wickUpColor: '#22c55e',
        wickDownColor: '#f87171',
        priceLineVisible: showDashed ? true : false,
      });
      mainSeries.setData(calculateHeikinAshi(data));
    }



    // Store the main series reference
    chartInstance.current.mainSeries = mainSeries;

    // --- ADD POINT MARKER ---
    // จุด marker ที่ time:1755038231, price:193.37586151125112
    // const markerSeries = chart.addLineSeries({
    //   color: 'rgba(0,0,0,0)', // transparent line
    //   lineWidth: 1,
    // });
    // // ต้องใส่ value เพื่อให้จุด marker โชว์ที่ราคาที่ต้องการ
    // markerSeries.setData([
    //   { time: 1755038231 as UTCTimestamp, value: 193.37586151125112 }
    // ]);
    // markerSeries.setMarkers([
    //   {
    //     time: 1755038231 as UTCTimestamp,
    //     value: 193.37586151125112,
    //     position: 'aboveBar',
    //     color: '#ef4444',
    //     shape: 'circle',
    //   } as any,
    // ]);

    // --- ADD POINT MARKER ---
    // จุด marker ที่ time:1755038231, price:193.37586151125112





    // Calculate EMAs from full data
    // 🌟 คำนวณ EMA จาก data ตรงๆ เสมอ (แท่งเทียนที่กำลังพล็อตอยู่จริง ซึ่ง resolution ตรงกับ
    // timeframe ที่เลือกอยู่แล้ว) ไม่ใช้ historicalData (รายวันเสมอ) เพราะจะทำให้ EMA(200) บนกราฟรายสัปดาห์
    // กลายเป็น EMA(200) รายวันไปโดยไม่ตั้งใจ (period ควรมีความหมายตรงกับ timeframe ที่แสดงอยู่จริงเสมอ:
    // "200" บน week = 200 แท่งรายสัปดาห์, บน day = 200 แท่งรายวัน) นอกจากนี้การเอา historicalData
    // (รายวัน) มาพล็อตทับกราฟรายสัปดาห์ตรงๆ จะได้ time point รายวันปนอยู่บน time scale เดียวกับแท่งเทียน
    // รายสัปดาห์ ทำให้ lightweight-charts ต้องเผื่อพื้นที่ตาม timestamp รายวันที่ไม่มีแท่งเทียนจริง
    // เห็นเป็นช่องว่างห่างๆ ระหว่างแท่ง และทำให้ auto-scale ของแกน X เพี้ยนไปด้วย
    const emas = calculateAllEMAs(data);




    // Add EMA lines (always full data)
    if (emas.ema50.length > 0) {
      const ema50Series = chart.addSeries(LineSeries, {
        color: theme === 'dark' ? '#fff' : '#374151', // <-- เปลี่ยนตรงนี้
        lineWidth: 1,  // บางสุด
        lineStyle: 0,  // เส้นธรรมดา
        title: showOverlays ? 'EMA 50' : '',
        priceLineVisible: showDashed ? true : false,
        lastValueVisible: showOverlays ? true : false,
      });
      ema50Series.setData(
        emas.ema50.map((d) => ({ time: d.time as UTCTimestamp, value: d.value }))
      );
    }

    if (emas.ema100.length > 0) {
      const ema100Series = chart.addSeries(LineSeries, {
        color: '#f472b6', // ชมพูอ่อน
        lineWidth: 2,     // หนาขึ้น
        lineStyle: 1,     // เส้นประ
        title: showOverlays ? 'EMA 100' : '',
        priceLineVisible: showDashed ? true : false,
        lastValueVisible: showOverlays ? true : false,
      });
      ema100Series.setData(
        emas.ema100.map((d) => ({ time: d.time as UTCTimestamp, value: d.value }))
      );
    }

    if (emas.ema200.length > 0) {
      const ema200Series = chart.addSeries(LineSeries, {
        color: '#fbbf24', // เหลือง
        lineWidth: 2,
        lineStyle: 0,     // เส้นธรรมดา
        title: showOverlays ? 'EMA 200' : '',
        priceLineVisible: showDashed ? true : false,
        lastValueVisible: showOverlays ? true : false,
      });

      ema200Series.setData(
        emas.ema200.map((d) => ({ time: d.time as UTCTimestamp, value: d.value }))
      );
    }


    // // หลังจากคำนวณ emas แล้ว (มี emas.ema50)
    // if (emas.ema50.length > 0) {
    //   // สร้าง ribbon gradient ตามแนวโน้ม
    //   const ribbonData = data.map((d, i) => {
    //     const ema50 = emas.ema50[i]?.value ?? d.c;
    //     return {
    //       time: Math.floor(d.t / 1000) as UTCTimestamp,
    //       value: d.c,
    //       uptrend: d.c >= ema50,
    //     };
    //   });

    //   // วาด ribbon 2 ชุด: ขาขึ้น (เขียว), ขาลง (แดง)
    //   const uptrendSeries = chart.addAreaSeries({
    //     topColor: 'rgba(34,197,94,0.15)',    // เขียวจาง
    //     bottomColor: 'rgba(0,0,0,0)',
    //     lineColor: 'rgba(0,0,0,0)',
    //     lineWidth: 1,
    //   });
    //   uptrendSeries.setData(
    //     ribbonData.filter(d => d.uptrend).map(d => ({
    //       time: d.time,
    //       value: d.value,
    //     }))
    //   );

    //   const downtrendSeries = chart.addAreaSeries({
    //     topColor: 'rgba(239,68,68,0.15)',    // แดงจาง
    //     bottomColor: 'rgba(0,0,0,0)',
    //     lineColor: 'rgba(0,0,0,0)',
    //     lineWidth: 1,
    //   });
    //   downtrendSeries.setData(
    //     ribbonData.filter(d => !d.uptrend).map(d => ({
    //       time: d.time,
    //       value: d.value,
    //     }))
    //   );
    // }



    // --- ADD THIS BLOCK FOR BOLLINGER BANDS ---
    if (showBollinger) {
      const bands = calculateBollingerBands(data);
      if (bands.length > 0) {
        // Upper Band
        const upperSeries = chart.addSeries(LineSeries, {
          color: '#60a5fa', // blue-400
          lineWidth: 1,
          title: showOverlays ? 'Bollinger Upper' : '',
          priceLineVisible: showDashed ? true : false,
        });
        upperSeries.setData(bands.map(b => ({ time: b.time as UTCTimestamp, value: b.upper })));

        // Middle Band
        const middleSeries = chart.addSeries(LineSeries, {
          color: '#fbbf24', // yellow-400
          lineWidth: 1,
          title: showOverlays ? 'Bollinger Middle' : '',
          priceLineVisible: showDashed ? true : false,
        });
        middleSeries.setData(bands.map(b => ({ time: b.time as UTCTimestamp, value: b.middle })));

        // Lower Band
        const lowerSeries = chart.addSeries(LineSeries, {
          color: '#60a5fa', // blue-400
          lineWidth: 1,
          title: showOverlays ? 'Bollinger Lower' : '',
          priceLineVisible: showDashed ? true : false,
        });
        lowerSeries.setData(bands.map(b => ({ time: b.time as UTCTimestamp, value: b.lower })));
      }
    }
    // --- END BOLLINGER BANDS BLOCK ---


    // Focus chart on selected zoomScope (ทำงานทุกโหมด)
    // 🌟 เช็คก่อนว่าเป็น "ชุดข้อมูลใหม่จริง" (เปลี่ยนหุ้น/เปลี่ยน frequency) หรือแค่ toggle
    // ปุ่ม/ธีม/ชนิดกราฟที่ทำให้ effect นี้ยิงซ้ำทั้งที่ data ตัวเดิม
    const datasetKey = `${symbol ?? ''}|${frequency ?? ''}|${data.length}|${data[0]?.t}|${data[data.length - 1]?.t}`;
    const isNewDataset = lastDatasetKeyRef.current !== datasetKey;
    lastDatasetKeyRef.current = datasetKey;

    if (!isNewDataset && preservedLogicalRange) {
      // ข้อมูลชุดเดิม (แค่ toggle) -> คืนค่าตำแหน่ง pan/zoom เดิม แทนที่จะคำนวณ range ใหม่
      // การเรียก setVisibleLogicalRange จะยิง subscribeVisibleLogicalRangeChange ด้วย
      // ซึ่งจะ sync ตำแหน่งนี้ไปยัง RSI/MACD ให้ตรงกันโดยอัตโนมัติ (ผ่าน onTimeRangeChange)
      try {
        chart.timeScale().setVisibleLogicalRange(preservedLogicalRange);
      } catch (err) {
        // Prevent crash
      }
    } else if (data.length > 0) {
      // ชุดข้อมูลใหม่จริง -> คำนวณ default range จาก zoomScope ตามเดิม
      const lastDate = new Date(data[data.length - 1].t);
      let fromDate = new Date(lastDate);
      switch (zoomScope) {
        case '1D':
          fromDate.setDate(lastDate.getDate() - 1);
          break;
        case '5D':
          fromDate.setDate(lastDate.getDate() - 5);
          break;
        case '1M':
          fromDate.setMonth(lastDate.getMonth() - 1);
          break;
        case '6M':
          fromDate.setMonth(lastDate.getMonth() - 6);
          break;
        case 'YTD':
          fromDate = new Date(lastDate.getFullYear(), 0, 1);
          break;
        case '1Y':
          fromDate.setFullYear(lastDate.getFullYear() - 1);
          break;
        case '5Y':
          fromDate.setFullYear(lastDate.getFullYear() - 5);
          break;
        default:
          fromDate.setMonth(lastDate.getMonth() - 6);
      }
      // Find the first index >= fromDate
      const firstIdx = data.findIndex(d => new Date(d.t) >= fromDate);
      if (firstIdx !== -1) {
        const fromTime = Math.floor(data[firstIdx].t / 1000) as UTCTimestamp;
        const toTime = Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp;
        try {
          if (
            chartInstance.current &&
            typeof chartInstance.current.timeScale === 'function'
          ) {
            const timeScale = chartInstance.current?.timeScale?.();
            if (
              timeScale &&
              typeof timeScale.setVisibleRange === 'function'
            ) {
              timeScale.setVisibleRange({
                from: fromTime,
                to: toTime
              });
              visibleRangeSetRef.current = true;
            }
          }
        } catch (err) {
          // Prevent crash
        }
      }
    }




    // เส้น S/R แบบ freestyle (เส้นหนา) ย้ายไปวาดใน effect "sync เส้น S/R" แยกต่างหาก
    // เพราะ effect นี้ไม่ได้ผูกกับ lines/supportTags/resistanceTags ถ้าเส้นโหลดจาก localStorage
    // เสร็จทีหลังชาร์ตถูกสร้าง เส้นหนาจะไม่ถูกวาดเลยจนกว่าจะมีอะไรมาทำให้ชาร์ตสร้างใหม่


    // เมื่อ priceLineType เปลี่ยนเป็น freestyle ให้โหลด trendLines และ areas จาก localStorage
    if (priceLineType === 'freestyle') {
      // โหลด trendLines
      const savedTrendLines = localStorage.getItem('trendLines');
      if (savedTrendLines) {
        try {
          setTrendLines(JSON.parse(savedTrendLines));
        } catch (e) { }
      }
      // โหลด areas
      const savedAreas = localStorage.getItem('areas');
      if (savedAreas) {
        try {
          setAreas(JSON.parse(savedAreas));
        } catch (e) { }
      }
    }

    // ...existing code...

    function findMostTouchedLevels(data: OHLCData[], step = 0.05): number[] {
      // step: price granularity (e.g., 0.05 for stocks)
      if (!data.length) return [];
      const priceCount: Record<string, number> = {};

      // Find min/max price in data
      let minPrice = data[0].l, maxPrice = data[0].h;
      data.forEach(bar => {
        minPrice = Math.min(minPrice, bar.l);
        maxPrice = Math.max(maxPrice, bar.h);
      });

      // Create price bins
      for (let price = minPrice; price <= maxPrice; price += step) {
        priceCount[price.toFixed(2)] = 0;
      }

      // Count touches for each candle
      data.forEach(bar => {
        [bar.o, bar.h, bar.l, bar.c].forEach(val => {
          const key = (Math.round(val / step) * step).toFixed(2);
          if (priceCount[key] !== undefined) priceCount[key]++;
        });
      });

      // Find the highest touch count
      const maxTouches = Math.max(...Object.values(priceCount));
      // Return all price levels with maxTouches
      return Object.entries(priceCount)
        .filter(([_, count]) => count === maxTouches)
        .map(([price]) => parseFloat(price));
    }






    // --- AUTO SUPPORT/RESISTANCE/AREA ---
    if (priceLineType === 'support-resistance') {
      // Filter data by zoomScope for S/R calculation
      const filteredData = filterDataByRange(data, zoomScope);
      // console.log('Filtered Data for S/R:', filteredData);
      // console.log('Calculating S/R levels...', data);
      // console.log('Data length:', zoomScope);
      const { supports, resistances } = findLowHighSR(filteredData, 1, 3); // step=1, topN=3
      // setSrLines([
      //   ...supports.map((price, idx) => ({
      //     tag: `S${idx + 1}`,
      //     price,
      //     type: 'support' as 'support',
      //     startY: chartInstance.current.mainSeries.priceToCoordinate(price),
      //     endY: chartInstance.current.mainSeries.priceToCoordinate(price),
      //   })),
      //   ...resistances.map((price, idx) => ({
      //     tag: `R${idx + 1}`,
      //     price,
      //     type: 'resistance' as 'resistance',
      //     startY: chartInstance.current.mainSeries.priceToCoordinate(price),
      //     endY: chartInstance.current.mainSeries.priceToCoordinate(price),
      //   })),
      // ]);
      // สร้าง srLines ใหม่
      const newSrLines = [
        ...supports.map((price, idx) => ({
          tag: `S${idx + 1}`,
          price,
          type: 'support' as 'support',
          startY: chartInstance.current?.mainSeries?.priceToCoordinate(price),
          endY: chartInstance.current?.mainSeries?.priceToCoordinate(price),
        })),
        ...resistances.map((price, idx) => ({
          tag: `R${idx + 1}`,
          price,
          type: 'resistance' as 'resistance',
          startY: chartInstance.current?.mainSeries?.priceToCoordinate(price),
          endY: chartInstance.current?.mainSeries?.priceToCoordinate(price),
        })),
      ];

      // เช็คว่า srLines เปลี่ยนจริงค่อย set
      if (JSON.stringify(srLines) !== JSON.stringify(newSrLines)) {
        setSrLines(newSrLines);
        if (onSupportResistanceUpdate) {
          onSupportResistanceUpdate(newSrLines.map(l => ({ tag: l.tag, price: l.price })));
        }
      }

      // Draw POC line (red)
      // if (poc && !isNaN(poc)) {
      //   const pocLine = chart.addLineSeries({
      //     color: '#ef4444', // red
      //     lineWidth: 2,
      //     title: 'POC',
      //   });
      //   pocLine.setData([
      //     { time: Math.floor(filteredData[0].t / 1000) as UTCTimestamp, value: poc },
      //     { time: Math.floor(filteredData[filteredData.length - 1].t / 1000) as UTCTimestamp, value: poc },
      //   ]);
      // }






      // Draw resistance lines (purple)
      if (showRLine) {
        resistances.forEach((price, idx) => {
          const tag = `R${idx + 1}`;
          addOrUpdateSRLine(tag, price, 'resistance');
          if (onSupportResistanceUpdate) {
            onSupportResistanceUpdate({ tag, price });
          }
        });
      }
      if (showSLine) {
        supports.forEach((price, idx) => {
          const tag = `S${idx + 1}`;
          addOrUpdateSRLine(tag, price, 'support');
          if (onSupportResistanceUpdate) {
            onSupportResistanceUpdate({ tag, price });
          }
        });
      }

      // --- Draw trend lines (highest to present, lowest to present) ---
      // --- Draw true trend lines (not crossing candles, bold red) ---



      // --- VOLUME PROFILE S/R (POPULAR PRICE BINS) ---
      // Add S/R lines based on most popular price bins (volume profile style)

      // Draw resistance lines (purple)


      // Draw support lines (yellow)

      // --- END VOLUME PROFILE S/R ---

      // // Focus chart on selected zoomScope
      // if (data.length > 0) {
      //   const lastDate = new Date(data[data.length - 1].t);
      //   let fromDate = new Date(lastDate);
      //   switch (zoomScope) {
      //     case '1D':
      //       fromDate.setDate(lastDate.getDate() - 1);
      //       break;
      //     case '5D':
      //       fromDate.setDate(lastDate.getDate() - 5);
      //       break;
      //     case '1M':
      //       fromDate.setMonth(lastDate.getMonth() - 1);
      //       break;
      //     case '6M':
      //       fromDate.setMonth(lastDate.getMonth() - 6);
      //       break;
      //     case 'YTD':
      //       fromDate = new Date(lastDate.getFullYear(), 0, 1);
      //       break;
      //     case '1Y':
      //       fromDate.setFullYear(lastDate.getFullYear() - 1);
      //       break;
      //     case '5Y':
      //       fromDate.setFullYear(lastDate.getFullYear() - 5);
      //       break;
      //     default:
      //       fromDate.setMonth(lastDate.getMonth() - 6);
      //   }
      //   // Find the first index >= fromDate
      //   const firstIdx = data.findIndex(d => new Date(d.t) >= fromDate);
      //   if (firstIdx !== -1) {
      //     const fromTime = Math.floor(data[firstIdx].t / 1000) as UTCTimestamp;
      //     const toTime = Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp;
      //     try {
      //       if (
      //         chartInstance.current &&
      //         typeof chartInstance.current.timeScale === 'function'
      //       ) {
      //         const timeScale = chartInstance.current?.timeScale?.();
      //         // เช็คว่า timeScale มีจริง และมี setVisibleRange
      //         if (
      //           timeScale &&
      //           typeof timeScale.setVisibleRange === 'function'
      //         ) {
      //           timeScale.setVisibleRange({
      //             from: fromTime,
      //             to: toTime
      //           });
      //           visibleRangeSetRef.current = true; // Only set once
      //         }
      //       }
      //     } catch (err) {
      //       // Prevent crash, optionally log error
      //       // console.warn('Failed to set visibleRange:', err);
      //     }
      //   }
      // }


      // --- Draw avgBuyPrice line ---
      // if (typeof avgBuyPrice === 'number' && !isNaN(avgBuyPrice)) {
      //   const avgLine = chart.addLineSeries({
      //     color: '#06b6d4', // cyan-500
      //     lineWidth: 2,
      //     lineStyle: 2, // 2 = dashed in lightweight-charts
      //     title: 'ต้นทุนเฉลี่ย',
      //   });
      //   avgLine.setData([
      //     { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: avgBuyPrice },
      //     { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: avgBuyPrice },
      //   ]);
      // }

      return () => {
        if (chartInstance.current) {
          chartInstance.current.timeScale().unsubscribeVisibleLogicalRangeChange(logicalRangeHandler);
          chartInstance.current.remove();
          chartInstance.current = null;
        }
      };
    }

  }
    , [data, historicalData, chartType, zoomScope, showOverlays, showDashed, showBollinger, priceLineType, avgBuyPrice, showSLine, showRLine, showTrendline, visibleRangeVersion, theme, frequency]);






  useEffect(() => {
    if (!chartInstance.current || priceLineType !== 'support-resistance') return;

    const interval = setInterval(() => {
      // Check chart validity before drawing
      if (!chartInstance.current || !chartInstance.current.addSeries) return;

      const visible = getVisibleRange();
      let trendFilteredData = data;
      if (visible) {
        trendFilteredData = data.filter((d: OHLCData) => {
          const t = Math.floor(d.t / 1000);
          return t >= visible.from && t <= visible.to;
        });
      }

      // Remove previous trendlines if exist
      if (chartInstance.current.trendLineSeriesHigh) {
        chartInstance.current.removeSeries(chartInstance.current.trendLineSeriesHigh);
        chartInstance.current.trendLineSeriesHigh = null;
      }
      if (chartInstance.current.trendLineSeriesLow) {
        chartInstance.current.removeSeries(chartInstance.current.trendLineSeriesLow);
        chartInstance.current.trendLineSeriesLow = null;
      }

      // Draw new strict trendlines
      if (showTrendline && trendFilteredData.length > 1) {
        // Top (strict downtrend)
        const strictDowntrend = findStrictDowntrendLine(trendFilteredData);
        if (strictDowntrend) {
          const strictDownSeries = chartInstance.current.addSeries(LineSeries, {
            color: '#FF5F1F',
            lineWidth: 2,
            lineStyle: 1,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          strictDownSeries.setData([strictDowntrend.start, strictDowntrend.end]);
          chartInstance.current.trendLineSeriesHigh = strictDownSeries;
        }
        // Bottom (strict uptrend)
        const strictUptrend = findStrictUptrendLine(trendFilteredData);
        if (strictUptrend) {
          const strictUpSeries = chartInstance.current.addSeries(LineSeries, {
            color: '#FF5F1F',
            lineWidth: 2,
            lineStyle: 1,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          strictUpSeries.setData([strictUptrend.start, strictUptrend.end]);
          chartInstance.current.trendLineSeriesLow = strictUpSeries;
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [data, priceLineType, showTrendline]);

  // Lower indicator chart
  useEffect(() => {
    if (!lowerRef.current || !lowerIndicatorData.length) return;
    const initialWidth = Math.floor(lowerRef.current.getBoundingClientRect().width) || 800;
    const chart = createChart(lowerRef.current, {
      width: initialWidth,
      height: 180,
      layout: {
        background: { type: ColorType.Solid, color: theme === 'dark' ? '#18181b' : '#ffffff' },
        textColor: theme === 'dark' ? '#e5e7eb' : '#000000', // Adjust text color for light theme
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#27272a' : '#e0e0e0' }, // Adjust grid line color
        horzLines: { color: theme === 'dark' ? '#27272a' : '#e0e0e0' },
      },
      crosshair: {
        vertLine: { color: '#facc15', width: 1 },
        horzLine: { color: '#facc15', width: 1 },
        mode: 0,
      },
    });
    const lineSeries = chart.addSeries(LineSeries, { color: '#f472b6' });
    lineSeries.setData(lowerIndicatorData.map(d => ({ time: d.time as UTCTimestamp, value: d.value })));

    // 🌟 ชาร์ตล่าง (RSI/MACD) เดิม fix width ไว้ที่ 800 ตายตัว เลยไม่ตามความกว้างคอลัมน์
    // ที่ลาก resize ได้ ต้องมี ResizeObserver ของตัวเองแยกจากชาร์ตหลัก
    const handleLowerResize = () => {
      if (!lowerRef.current) return;
      const width = Math.floor(lowerRef.current.getBoundingClientRect().width);
      if (width) chart.resize(width, 180);
    };
    const lowerObserver = new ResizeObserver(handleLowerResize);
    lowerObserver.observe(lowerRef.current);

    return () => {
      lowerObserver.disconnect();
      chart.remove();
    };
  }, [lowerIndicatorData]);

  // Calculate current EMA values for legend
  // Always use the full historicalData (or data) for EMA calculation, not filtered by range
  const emaSource = historicalData && historicalData.length > 0 ? historicalData : data;
  // 🌟 ต้องใช้ data ตรงๆ เหมือนตอนวาดเส้น EMA บนกราฟ (ดูคอมเมนต์ในเอฟเฟกต์สร้างกราฟด้านบน) ไม่งั้นเลข
  // EMA ที่โชว์ใน legend/ส่งออกไปใน onIndicatorsReady จะไม่ตรงกับเส้นที่วาดจริงบนกราฟ
  const emas = calculateAllEMAs(data);

  const currentPrice = emaSource.length > 0 ? emaSource[emaSource.length - 1].c : 0;
  // 🌟 currentPrice มาจาก data/historicalData ซึ่งอัปเดตทีหลัง symbol เสมอ (คนละ prop, มาจาก fetch คนละรอบ)
  // ถ้าเอา currentPrice ไปใส่ dependency ของ effect โหลดข้อมูล S/R ด้านล่าง จะทำให้ effect
  // นั้นยิงซ้ำ 2 รอบต่อการเปลี่ยนหุ้นหนึ่งครั้ง (รอบแรกด้วยราคาหุ้นตัวเก่าที่ยังค้างอยู่ รอบสอง
  // ด้วยราคาจริงของหุ้นใหม่) จึงเก็บค่าล่าสุดไว้ใน ref แทน เพื่ออ่านตอน loadData() โดยไม่ต้อง
  // ทำให้ effect ยิงซ้ำ
  const currentPriceRef = useRef(currentPrice);
  currentPriceRef.current = currentPrice;
  const currentEma50 = emas.ema50.length > 0 ? emas.ema50[emas.ema50.length - 1].value : 0;
  const currentEma100 = emas.ema100.length > 0 ? emas.ema100[emas.ema100.length - 1].value : 0;
  const currentEma200 = emas.ema200.length > 0 ? emas.ema200[emas.ema200.length - 1].value : 0;

  // console.log("emas", emas, "currentPrice", currentPrice, currentEma50, currentEma100, currentEma200);
  const handleToolClick = (tool: 'drag' | 'line' | 'sr-line' | 'area') => {
    setActiveTool(tool);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!chartRef.current) return;
    isDragging = false; // Reset dragging flag

    const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
    const startY = event.clientY - rect.top;

    // Check if the user clicked on a support or resistance line
    const clickedLine = (
      [
        ...supportTags.map((tag) => ({ tag, type: 'support' as const })),
        ...resistanceTags.map((tag) => ({ tag, type: 'resistance' as const })),
      ] as { tag: string; type: 'support' | 'resistance' }[]
    ).find((line) => {
      const lineData = lines.find((l) => l.tag === line.tag);
      if (!lineData) return false;
      const lineY = lineData.startY;
      return Math.abs(lineY - startY) < 5; // Check if the click is near the line
    });

    if (clickedLine) {
      setDraggingLine(clickedLine);
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!chartRef.current || !draggingLine) return;
    isDragging = true; // Set dragging flag

    const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
    const y = event.clientY - rect.top;

    // Convert y-coordinate to price using the main series
    if (!chartInstance.current || !chartInstance.current.mainSeries) return;
    const mainSeries = chartInstance.current.mainSeries;
    const price = mainSeries.coordinateToPrice(y);

    // Update the dragged line's position and price
    setLines((prev) =>
      prev.map((line) =>
        line.tag === draggingLine.tag
          ? { ...line, startY: y, endY: y, price } // <-- price updates here
          : line
      )
    );
  };

  const handleMouseUp = () => {
    setDraggingLine(null); // Stop dragging
    setDraggingTag(null);
  };
  const handleDelete = (tag: string) => {
    if (isDragging) return;

    // 1. กรองเส้นที่ต้องการลบออกก่อน
    const filtered = lines.filter((line) => line.tag !== tag);

    // 2. เรียกใช้ฟังก์ชันที่ปรับปรุงใหม่ (คืนค่าเป็น Object)
    // ✅ ต้องใส่ currentPrice เข้าไปด้วยนะพี่
    const result = reorderSRLines(filtered, currentPrice);

    // ดึง Array lines ออกมาเพื่อวาดกราฟ
    const newLines = result.lines;

    // 3. ล้าง Series เก่าออกจากกราฟ (Logic เดิม)
    lineSeriesMap.current.forEach((series) => {
      if (chartInstance.current) {
        try { chartInstance.current.removeSeries(series); } catch (e) { }
      }
    });
    lineSeriesMap.current.clear();

    // 4. วาดเส้นชุดใหม่ลงไป
    newLines.forEach((line) => {
      addOrUpdateSRLine(line.tag, line.price, line.type);
    });

    // 5. อัปเดต State ต่างๆ โดยใช้ค่าจาก result
    setLines(newLines);
    setSupportTags(result.supportTags);
    setResistanceTags(result.resistanceTags);

    // อัปเดต Counter โดยใช้ Array จาก result.lines
    supportCounterRef.current = result.supportTags.length + 1;
    resistanceCounterRef.current = result.resistanceTags.length + 1;

    // 6. แจ้ง Parent ด้วยข้อมูลล่าสุดที่ Map เรียบร้อยแล้ว
    if (onSupportResistanceUpdate) {
      onSupportResistanceUpdate(newLines.map((l: any) => ({ tag: l.tag, price: l.price })));
    }
  };

  const handleDoubleClick = (event: React.MouseEvent | null, manualX?: number, manualY?: number) => {
    if (!chartRef.current || activeTool !== 'sr-line') return;

    const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
    const y = manualY !== undefined ? manualY : (event ? event.clientY - rect.top : 0);

    if (!chartInstance.current || !chartInstance.current.mainSeries) return;
    const mainSeries = chartInstance.current.mainSeries;
    const price = mainSeries.coordinateToPrice(y);

    if (price === null || isNaN(price)) return;

    // ✅ 1. ตัดสินประเภทเส้นหน้างานจริงๆ ตามราคาปัจจุบัน
    // แม้พี่จะเลือกเครื่องมือ S แต่ถ้าจิ้มเหนือราคาปัจจุบัน มันต้องเป็นแนวต้าน (resistance)
    const isActuallySupport = price <= currentPrice;
    const determinedType = isActuallySupport ? 'support' : 'resistance';

    // 2. สร้างเส้นใหม่ (ใช้ ID ชั่วคราวเพื่อให้ reorderSRLines ไปเจนชื่อ S1, R1 ให้ใหม่)
    const newLine = {
      tag: `TEMP_${Date.now()}`,
      price: Number(price.toFixed(2)),
      type: determinedType, // ✅ ใช้ประเภทที่เราคำนวณใหม่แทน activeTool
      startX: 0,
      startY: y,
      endX: chartSize.width,
      endY: y,
    };

    // 3. รวมเส้นเดิมและส่งไปจัดลำดับใหม่ด้วยฟังก์ชันที่เราแก้กันไปก่อนหน้านี้
    // ✅ ต้องส่ง currentPrice เข้าไปด้วยเพื่อให้ได้ Object { lines, supportTags, resistanceTags }
    const result = reorderSRLines([...lines, newLine], currentPrice);
    const finalReorderedLines = result.lines;

    // 4. ล้างเส้นเก่าออกจากกราฟทั้งหมด (Logic เดิมของพี่)
    lineSeriesMap.current.forEach((series) => {
      if (chartInstance.current) {
        try { chartInstance.current.removeSeries(series); } catch (e) { }
      }
    });
    lineSeriesMap.current.clear();

    // 5. อัปเดต State ทุกตัวให้ Sync กัน (ใช้ค่าจาก result)
    setLines(finalReorderedLines);
    setSupportTags(result.supportTags);
    setResistanceTags(result.resistanceTags);

    // 6. วาดเส้นหนาลงกราฟใหม่ทุกลำดับ
    finalReorderedLines.forEach((line) => {
      addOrUpdateSRLine(line.tag, line.price, line.type as any);
    });

    // 7. แจ้ง Parent (HomePage)
    if (onSupportResistanceUpdate) {
      onSupportResistanceUpdate(
        finalReorderedLines.map((l: any) => ({ tag: l.tag, price: l.price }))
      );
    }

    // 8. เปิดการแสดงผลเส้นตามประเภทที่สร้างขึ้นจริง
    if (determinedType === 'support' && setShowSLine) setShowSLine(true);
    if (determinedType === 'resistance' && setShowRLine) setShowRLine(true);
  };

  const [isInitialized, setIsInitialized] = useState(false);
  const currentSymbolRef = useRef(symbol);

  // --- 1. Effect สำหรับ LOAD ข้อมูล ---
  // --- 1. Effect สำหรับ LOAD ข้อมูล (ปรับปรุงใหม่) ---
  // --- 1. Effect สำหรับ LOAD ข้อมูล (ปรับปรุงใหม่ให้ทำงานเป็นลำดับ) ---
  // --- 1. Effect สำหรับ LOAD ข้อมูล (ปรับปรุงใหม่ให้ทำงานเป็นลำดับ) ---
  // 🛡️ ตัวแปรเช็คสถานะ เพื่อไม่ให้เซฟข้อมูลมั่วซั่วตอนเปลี่ยนหุ้น
  const isDataLoadedRef = useRef(false);

  // --- 1. Effect สำหรับ LOAD ข้อมูล ---
  useEffect(() => {
    if (!symbol) return;

    setIsInitialized(false);
    isDataLoadedRef.current = false; // ล็อก ห้ามเซฟระหว่างกำลังโหลดเด็ดขาด

    // เคลียร์กราฟเก่าทิ้งทันที
    if (chartInstance.current) {
      lineSeriesMap.current.forEach(series => {
        try { chartInstance.current.removeSeries(series); } catch (e) { }
      });
      lineSeriesMap.current.clear();
    }

    // ล้าง State ชั่วคราวก่อนเพื่อไม่ให้เห็นของเก่าค้าง
    setLines([]);
    setSupportTags([]);
    setResistanceTags([]);
    setTrendLines([]);
    setAreas([]);

    const loadData = () => {
      const storageKey = `chartLines_${symbol}`;
      const saved = localStorage.getItem(storageKey);

      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const rawLines = parsed.lines || [];

          const targetPrice = currentPriceRef.current > 0 ? currentPriceRef.current : (rawLines[0]?.price || 0);

          if (targetPrice > 0 && rawLines.length > 0) {
            const result = reorderSRLines(rawLines, targetPrice);

            setLines(result.lines);
            setSupportTags(result.supportTags);
            setResistanceTags(result.resistanceTags);

            supportCounterRef.current = result.supportTags.length + 1;
            resistanceCounterRef.current = result.resistanceTags.length + 1;

            if (onSupportResistanceUpdate) {
              onSupportResistanceUpdate(result.lines.map((l: any) => ({ tag: l.tag, price: l.price })));
            }
          } else {
            setLines([]);
            setSupportTags([]);
            setResistanceTags([]);
            if (onSupportResistanceUpdate) onSupportResistanceUpdate([]);
          }
        } catch (e) {
          console.error("Parse error:", e);
          setLines([]);
        }
      } else {
        // 🛑 ถ้าหุ้นตัวนี้ไม่มีข้อมูลใน LocalStorage จริงๆ ให้เคลียร์ค่าสำหรับหุ้นตัวนี้โดยเฉพาะ
        setLines([]);
        setSupportTags([]);
        setResistanceTags([]);
        supportCounterRef.current = 1;
        resistanceCounterRef.current = 1;
        if (onSupportResistanceUpdate) onSupportResistanceUpdate([]);
      }

      // โหลด Trendline และ Area เฉพาะของ Symbol นี้เท่านั้น (ถ้าไม่มีให้เป็นอาเรย์ว่าง)
      const savedTrends = localStorage.getItem(`trendLines_${symbol}`);
      setTrendLines(savedTrends ? JSON.parse(savedTrends) : []);

      const savedAreasFromStore = localStorage.getItem(`areas_${symbol}`);
      setAreas(savedAreasFromStore ? JSON.parse(savedAreasFromStore) : []);

      currentSymbolRef.current = symbol;

      // ✅ โหลดเสร็จสมบูรณ์ ค่อยปลดล็อกให้ระบบ Save ทำงานได้
      isDataLoadedRef.current = true;
    };

    loadData();

    // 🌟 ปลดล็อกการเซฟหลังจากโหลดเสร็จ (ดีเลย์กันเซฟไวเกินไประหว่าง state ยังไม่นิ่ง)
    // ต้อง clearTimeout ตอน effect ยิงซ้ำ/unmount ไม่งั้นถ้าเปลี่ยนหุ้นเร็วๆ (เช่น NVDA -> TSLA -> AAPL
    // ภายใน 150ms) timer ของรอบเก่าจะยังค้างอยู่แล้วมาปลดล็อกให้รอบใหม่ทั้งที่ยังโหลดไม่เสร็จจริง
    // ทำให้ระบบ Save เผลอเซฟ state ที่ยังไม่ถูกต้อง (ของหุ้นเก่า หรือค่าที่จัด support/resistance
    // ผิดเพราะยังใช้ราคาหุ้นเก่าอยู่) ทับ localStorage ของหุ้นใหม่ -> เกิดปัญหาเส้นหายไป/เส้นข้ามหุ้น
    const unlockTimer = setTimeout(() => {
      setIsInitialized(true);
    }, 150);

    return () => clearTimeout(unlockTimer);
    // 🌟 ตั้งใจไม่ใส่ currentPrice ใน deps เพราะมันอัปเดตทีหลัง symbol เสมอ (คนละ fetch) และ
    // จะทำให้ effect นี้ยิงซ้ำ 2 รอบต่อการเปลี่ยนหุ้นหนึ่งครั้งโดยไม่จำเป็น (ดูคอมเมนต์ที่ currentPriceRef)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // --- 1b. Effect สำหรับจัด support/resistance ใหม่ เมื่อราคาจริงของหุ้นตัวปัจจุบันมาถึงทีหลัง ---
  // ไม่ล้าง state / ไม่ยุ่งกับ isInitialized หรือ isDataLoadedRef เพื่อไม่ให้ไปรีเซ็ตระบบ Save ซ้ำ
  useEffect(() => {
    if (!symbol || symbol !== currentSymbolRef.current) return;
    if (!isDataLoadedRef.current) return;
    if (currentPrice <= 0 || lines.length === 0) return;

    const result = reorderSRLines(lines, currentPrice);
    const sameClassification = result.lines.length === lines.length &&
      result.lines.every((l: any, idx: number) => l.tag === lines[idx]?.tag && l.type === lines[idx]?.type);
    if (sameClassification) return;

    setLines(result.lines);
    setSupportTags(result.supportTags);
    setResistanceTags(result.resistanceTags);
  }, [currentPrice, symbol]);

  // --- 1c. Effect sync เส้น S/R (เส้นหนาที่ลากได้) ลงชาร์ต ---
  // แหล่งเดียวที่ทำให้เส้นหนาตรงกับ state เสมอ ไม่ว่าเส้นจะโหลดเสร็จก่อนหรือหลังชาร์ตถูกสร้าง
  // (เดิมวาดแค่ตอนสร้างชาร์ต ถ้าเส้นโหลดทีหลัง เส้นหนา + ปุ่ม ✕ จะหายจนกว่าจะกด option ให้ชาร์ตสร้างใหม่)
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !data.length) return;

    lineSeriesMap.current.forEach(series => {
      try { chart.removeSeries(series); } catch (e) { }
    });
    lineSeriesMap.current.clear();

    if (priceLineType !== 'freestyle') return;

    if (showSLine) {
      supportTags.forEach(tag => {
        const line = lines.find(l => l.tag === tag);
        if (line && typeof line.price === 'number') addOrUpdateSRLine(tag, line.price, 'support');
      });
    }
    if (showRLine) {
      resistanceTags.forEach(tag => {
        const line = lines.find(l => l.tag === tag);
        if (line && typeof line.price === 'number') addOrUpdateSRLine(tag, line.price, 'resistance');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartVersion, lines, supportTags, resistanceTags, showSLine, showRLine, showOverlays, priceLineType, currentPrice, data]);

  // --- 2. Effect สำหรับ SAVE ข้อมูล (ปลอดภัย ไม่ทับซ้อน) ---
  useEffect(() => {
    // 🛑 เงื่อนไขเหล็ก: ต้อง Initialize แล้ว, โหลดข้อมูลเสร็จแล้ว, และ Symbol ต้องตรงกันเป๊ะๆ เท่านั้นถึงจะเซฟ
    if (isInitialized && isDataLoadedRef.current && symbol && symbol === currentSymbolRef.current) {
      const dataToSave = { lines, supportTags, resistanceTags };

      localStorage.setItem(`chartLines_${symbol}`, JSON.stringify(dataToSave));
      localStorage.setItem(`trendLines_${symbol}`, JSON.stringify(trendLines));
      localStorage.setItem(`areas_${symbol}`, JSON.stringify(areas));
    }
  }, [lines, supportTags, resistanceTags, trendLines, areas, isInitialized, symbol]);


  function coordinateToTime(x: number) {
    if (!chartInstance.current) return null;
    const timeScale = chartInstance.current.timeScale();
    const range = timeScale.getVisibleRange();
    const width = chartSize.width;
    if (!range || !width) return null;

    // x = 0 -> range.from, x = width -> range.to
    const from = range.from;
    const to = range.to;
    const time = from + ((to - from) * x) / width;
    return Math.round(time); // UTCTimestamp
  }

  function coordinateToPrice(y: number) {
    if (!chartInstance.current || !chartInstance.current.mainSeries) return null;
    return chartInstance.current.mainSeries.coordinateToPrice(y);
  }


  const handleChartMouseDown = (event: React.MouseEvent | React.TouchEvent) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;

    // 1. ดึงพิกัดก่อน (ต้องใช้ทั้งตอน Double Tap และวาดปกติ)
    const clientX = 'touches' in event ? event.touches[0].clientX : (event as React.MouseEvent).clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : (event as React.MouseEvent).clientY;

    if (!chartRef.current) return;
    const rect = chartRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // 2. เช็คว่ากดโดน "ปุ่ม" หรือ "เมนูเครื่องมือ" หรือไม่
    // ถ้ากดโดนปุ่ม ให้ล้างเวลาทิ้ง (เพื่อไม่ให้นับจังหวะเปลี่ยนปุ่มเป็น Double Tap) แล้วจบการทำงาน
    const isButtonClick = (event.target as HTMLElement).tagName === 'BUTTON' ||
      (event.target as HTMLElement).closest('.tool-buttons') ||
      (event.target as HTMLElement).closest('.absolute.left-0.top-0'); // คลุมแผงเครื่องมือ

    if (isButtonClick) {
      lastTapRef.current = 0; // ล้างค่าทิ้ง
      return; // ไม่ต้องทำ Logic วาดเส้นต่อ
    }

    // 3. Logic Double Tap (ทำงานเฉพาะเมื่อกดบนกราฟ)
    if (lastTapRef.current > 0 && (now - lastTapRef.current < DOUBLE_TAP_DELAY)) {
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();

      handleDoubleClick(null, x, y);
      lastTapRef.current = 0; // Reset หลังวาดเสร็จ
      return;
    }

    // เก็บเวลาปัจจุบันไว้เช็คครั้งถัดไป (เฉพาะเมื่อกดบนพื้นที่ว่างของกราฟ)
    lastTapRef.current = now;

    // --- 3. ป้องกันการคลิกโดนปุ่มเครื่องมือ ---
    if (
      (event.target as HTMLElement).tagName === 'BUTTON' ||
      (event.target as HTMLElement).closest('.tool-buttons')
    ) {
      return;
    }

    // --- 4. จัดการ Tag Selection ---
    const tagName = (event.target as HTMLElement).tagName;
    if (tagName !== 'rect' && tagName !== 'line' && tagName !== 'circle' && tagName !== 'text') {
      setSelectedTrendLineTag(null);
      setSelectedAreaTag(null);
    }

    // --- 5. Logic การวาด Area (ใช้ x, y จากที่ประกาศไว้ข้างบน) ---
    if (activeTool === 'area' && priceLineType === 'freestyle') {
      const time = coordinateToTime(x);
      const price = coordinateToPrice(y);

      if (!isDrawingArea) {
        if (time !== null && price !== null) {
          setDrawingArea({
            startX: x, startY: y, endX: x, endY: y,
            startTime: time, startPrice: price,
            endTime: time, endPrice: price,
          });
          setIsDrawingArea(true);
        }
      } else if (drawingArea) {
        const endTime = coordinateToTime(x);
        const endPrice = coordinateToPrice(y);
        if (drawingArea.startTime !== undefined && drawingArea.startPrice !== undefined && endTime !== null && endPrice !== null) {
          const newArea = {
            startTime: drawingArea.startTime,
            startPrice: drawingArea.startPrice,
            endTime, endPrice,
            tag: `A${areas.length + 1}`,
          };
          setAreas(prev => [...prev, newArea]);
          setDrawingArea(null);
          setIsDrawingArea(false);
          setActiveTool('drag');
          return;
        }
      }
    }

    // --- 6. Logic การวาด Line ---
    if (activeTool === 'line' && priceLineType === 'freestyle') {
      const time = coordinateToTime(x);
      const price = coordinateToPrice(y);

      if (!isDrawingLine) {
        setDrawingLine({ startX: x, startY: y, endX: x, endY: y, startTime: time, startPrice: price });
        setIsDrawingLine(true);
      } else if (drawingLine) {
        const endTime = coordinateToTime(x);
        const endPrice = coordinateToPrice(y);
        const newTrendLine = {
          ...drawingLine,
          endX: x, endY: y,
          endTime, endPrice,
          tag: `L${Date.now()}`
        };
        setTrendLines((prev) => [...prev, newTrendLine]);
        setDrawingLine(null);
        setIsDrawingLine(false);
        setActiveTool('drag');
      }
    }
  };

  const handleChartMouseMove = (event: React.MouseEvent) => {
    // //console.log('handleChartMouseMove called', activeTool);
    if (activeTool === 'area' && isDrawingArea && drawingArea) {
      if (!chartRef.current) return;
      const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setDrawingArea({ ...drawingArea, endX: x, endY: y });
      // //console.log('Drawing area preview:', { ...drawingArea, endX: x, endY: y });
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    if (activeTool === 'line' && isDrawingLine && drawingLine) {

      if (!chartRef.current) return;
      const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setDrawingLine({ ...drawingLine, endX: x, endY: y });
      event.stopPropagation();
      event.preventDefault();
    }

  };

  useEffect(() => {
    if (chartRef.current) {
      const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
      setChartSize({ width: rect.width, height: MAIN_CHART_HEIGHT });
    }
  }, []);

  // 1. ฟังก์ชันที่ผูกกับปุ่มถังขยะเดิม
  const handleClearAll = () => {
    setIsClearConfirmOpen(true); // เปิด Modal ถามให้ชัวร์
  };

  // 2. ฟังก์ชันที่จะทำงานเมื่อกดยืนยันใน Modal
  const confirmClearAll = () => {
    // --- Logic การลบเดิมของพี่อั้ม ---
    lineSeriesMap.current.forEach(series => {
      if (chartInstance.current) chartInstance.current.removeSeries(series);
    });
    lineSeriesMap.current.clear();

    setLines(prev => prev.filter(line => line.type !== 'support' && line.type !== 'resistance'));
    setSupportTags([]);
    setResistanceTags([]);
    setAreas([]);
    setTrendLines([]);

    localStorage.removeItem('areas');
    localStorage.removeItem('trendLines');
    localStorage.removeItem(`chartLines_${symbol}`); // ลบของหุ้นตัวนั้นๆ ด้วย

    resistanceCounterRef.current = 1;
    supportCounterRef.current = 1;

    if (onClearAllSR) onClearAllSR();

    setIsClearConfirmOpen(false); // ปิด Modal
    toast.success("ล้างข้อมูลบนกราฟเรียบร้อยแล้ว");
  };

  // Whenever you add/remove a line, area, supportTag, resistanceTag:
  useEffect(() => {
    setHistory(prev => [
      ...prev,
      {
        lines,
        areas,
        supportTags,
        resistanceTags,
      }
    ]);
    // console.log("Hwww", resistanceTags, supportTags)
  }, [lines, areas, supportTags, resistanceTags]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        setHistory(prev => {
          if (prev.length < 2) return prev;
          const newHistory = prev.slice(0, -1);
          const last = newHistory[newHistory.length - 1];
          setLines(last.lines);
          setAreas(last.areas);

          setSupportTags(last.supportTags);
          setResistanceTags(last.resistanceTags);
          return newHistory;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);


  const bb = calculateBollingerBands(emaSource);
  const latestBB = bb.length > 0 ? bb[bb.length - 1] : { upper: 0, middle: 0, lower: 0 };

  const macd = calculateMACD(emaSource);
  let latestMACD: { value: number; signal: number; histogram: number };

  if (macd.length > 0) {
    const m = macd[macd.length - 1];
    if ('macd' in m && 'signal' in m && 'hist' in m) {
      latestMACD = {
        value: m.macd,
        signal: m.signal,
        histogram: m.hist,
      };
    } else {
      // fallback for already-normalized or other shape
      latestMACD = {
        value: (m as any).value ?? 0,
        signal: (m as any).signal ?? 0,
        histogram: (m as any).histogram ?? 0,
      };
    }
  } else {
    latestMACD = { value: 0, signal: 0, histogram: 0 };
  }

  const rsi = calculateRSI(emaSource);
  const latestRSI = rsi.length > 0 ? (rsi[rsi.length - 1].value ?? 0) : 0;

  const lastCandle = emaSource.length > 0 ? emaSource[emaSource.length - 1] : null;

  const lastSentRef = useRef<{ symbol?: string, close?: number } | null>(null);

  useEffect(() => {
    if (!onIndicatorsReady || !lastCandle) return;

    // Only call if symbol or lastCandle.close has changed
    if (
      lastSentRef.current &&
      lastSentRef.current.symbol === symbol &&
      lastSentRef.current.close === lastCandle.c
    ) {
      return; // Already sent for this symbol and candle
    }

    onIndicatorsReady({
      symbol,
      ohlcv: {
        open: lastCandle.o,
        high: lastCandle.h,
        low: lastCandle.l,
        close: lastCandle.c,
        volume: lastCandle.v,
        timestamp: new Date(lastCandle.t).toISOString(),
      },
      indicators: {
        ema50: { period: 50, value: currentEma50 },
        ema100: { period: 100, value: currentEma100 },
        ema200: { period: 200, value: currentEma200 },
        bb: {
          period: 20,
          stdDev: 2,
          upper: latestBB.upper,
          middle: latestBB.middle,
          lower: latestBB.lower,
        },
        macd: {
          fastPeriod: 12,
          slowPeriod: 26,
          signalPeriod: 9,
          macd: latestMACD.value,
          signal: latestMACD.signal,
          histogram: latestMACD.histogram,
        },
        rsi: {
          period: 14,
          value: latestRSI,
          thresholds: {
            overbought: 70,
            oversold: 30,
          }
        }
      }
    });

    // Update ref to prevent duplicate calls
    lastSentRef.current = { symbol, close: lastCandle.c };

  }, [
    symbol,
    lastCandle,
    currentEma50,
    currentEma100,
    currentEma200,
    latestBB,
    latestMACD,
    latestRSI,
    onIndicatorsReady
  ]);

  // Add this state for dragging by tag
  const [draggingTag, setDraggingTag] = useState<{ tag: string; type: 'support' | 'resistance' } | null>(null);
  let isDragging = false; // Flag to track dragging

  const handleTagMouseDown = (tag: string, type: 'support' | 'resistance', event?: React.MouseEvent | React.TouchEvent) => {
    if (isSRLocked) return;
    setDraggingTag({ tag, type });
    isDragging = false;

    // ถ้ามีการส่ง event มา (จาก Mobile) ให้ช่วยอัปเดตตำแหน่งทันที
    if (event) {
      const clientY = 'touches' in event ? event.touches[0].clientY : (event as React.MouseEvent).clientY;
      if (chartRef.current && chartInstance.current?.mainSeries) {
        const rect = chartRef.current.getBoundingClientRect();
        const y = clientY - rect.top;
        const price = chartInstance.current.mainSeries.coordinateToPrice(y);
        if (price !== null) {
          addOrUpdateSRLine(tag, price, type);
        }
      }
    }
  };

  // Call this function whenever you want to get the visible range
  function getVisibleRange() {
    if (chartInstance.current) {
      const range = chartInstance.current.timeScale().getVisibleRange();
      if (range) {
        return { from: range.from, to: range.to };
      }
    }
    return null;
  }

  const handleTagMouseMove = (event: React.MouseEvent | React.TouchEvent) => {
    if (!draggingTag || !chartRef.current) return;

    // ดึงค่า Y จาก Mouse หรือ Touch
    const clientY = 'touches' in event ? event.touches[0].clientY : (event as React.MouseEvent).clientY;

    const rect = (chartRef.current as HTMLElement).getBoundingClientRect();
    const y = clientY - rect.top;

    if (!chartInstance.current || !chartInstance.current.mainSeries) return;
    const mainSeries = chartInstance.current.mainSeries;
    const price = mainSeries.coordinateToPrice(y);

    if (price === null || isNaN(price)) return;

    // setLines((prev) =>
    //   prev.map((line) => {
    //     if (line.tag === draggingTag.tag) {
    //       return { ...line, startY: y, endY: y, price };
    //     }
    //     return line;
    //   })
    // );
    setLines((prev) => {
      const next = prev.map((line) => line.tag === draggingTag.tag ? { ...line, price } : line);
      // เช็คว่าราคาขยับจากที่บันทึกไว้ไหม
      return next;
    });

    addOrUpdateSRLine(draggingTag.tag, price, draggingTag.type);

    if (onSupportResistanceUpdate) {
      onSupportResistanceUpdate({ tag: draggingTag.tag, price });
    }
  };

  const handleTagMouseUp = () => {
    if (!draggingTag) {
      setDraggingTag(null);
      return;
    }

    let dataForParent: any[] = [];
    let nextSupportTags: string[] = [];
    let nextResistanceTags: string[] = [];

    // ✅ 1. หมุนเอาข้อมูลชุดจัดอันดับใหม่ออกมาคำนวณข้างนอกก่อน
    const result = reorderSRLines(lines, currentPrice);
    const newLines = result.lines;

    dataForParent = newLines.map((l) => ({ tag: l.tag, price: l.price }));
    nextSupportTags = result.supportTags;
    nextResistanceTags = result.resistanceTags;

    // ✅ 2. ล้าง Series เก่าที่ค้างอยู่บนหน้าจอ
    lineSeriesMap.current.forEach((series) => {
      if (chartInstance.current) {
        try { chartInstance.current.removeSeries(series); } catch (e) { }
      }
    });
    lineSeriesMap.current.clear();

    // ✅ 3. วาดเส้นใหม่ลง Lightweight Charts ทุกลำดับ
    newLines.forEach((line) => {
      addOrUpdateSRLine(line.tag, line.price, line.type as any);
    });

    // ✅ 4. อัปเดต React State ทุกก้อนแยกจากกันอย่างชัดเจนตามกฎระเบียบ
    setLines(newLines);
    setSupportTags(nextSupportTags);
    setResistanceTags(nextResistanceTags);

    // รีเซ็ตตัวนับลำดับคิวถัดไปให้สอดคล้องกับความจริง
    supportCounterRef.current = nextSupportTags.length + 1;
    resistanceCounterRef.current = nextResistanceTags.length + 1;

    // ✅ 5. แจ้ง Parent Component ด้วยข้อมูลชุดเสร็จสมบูรณ์
    if (onSupportResistanceUpdate) {
      setTimeout(() => {
        onSupportResistanceUpdate(dataForParent);
      }, 0);
    }

    setDraggingTag(null);
  };

  function timeToCoordinate(time: UTCTimestamp) {
    if (!chartInstance.current) return null;
    const timeScale = chartInstance.current.timeScale();
    if (typeof timeScale.timeToCoordinate === 'function') {
      return timeScale.timeToCoordinate(time);
    }
    const mainSeries = chartInstance.current.mainSeries;
    if (mainSeries && Array.isArray(mainSeries.bars)) {
      const idx = mainSeries.bars.findIndex((bar: { time: UTCTimestamp }) => bar.time === time);
      if (idx !== -1 && typeof timeScale.logicalToCoordinate === 'function') {
        return timeScale.logicalToCoordinate(idx);
      }
    }
    // fallback interpolation
    const range = timeScale.getVisibleRange();
    const width = chartSize.width;
    if (!range || !width) return null;
    const from = range.from;
    const to = range.to;
    if (to === from) return null;
    const x = ((time - from) / (to - from)) * width;
    return x;
  }
  function priceToCoordinate(price: number) {
    if (!chartInstance.current || !chartInstance.current.mainSeries) return null;
    return chartInstance.current.mainSeries.priceToCoordinate(price);
  }




  return (

    <div
      className="relative flex"
      style={{
        cursor:
          mode === 'damage-view'
            ? 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 50 50\'%3E%3Ccircle cx=\'25\' cy=\'25\' r=\'2\' fill=\'%23ff0000\'/%3E%3Cline x1=\'25\' y1=\'5\' x2=\'25\' y2=\'45\' stroke=\'%23ff0000\' stroke-width=\'2\'/%3E%3Cline x1=\'5\' y1=\'25\' x2=\'45\' y2=\'25\' stroke=\'%23ff0000\' stroke-width=\'2\'/%3E%3Ccircle cx=\'25\' cy=\'25\' r=\'10\' fill=\'none\' stroke=\'%23ff0000\' stroke-width=\'1\'/%3E%3C/svg%3E") 25 25, auto'
            : activeTool === 'drag'
              ? draggingLine
                ? 'grabbing'
                : 'grab'
              : activeTool === 'line' && isDrawingLine
                ? 'crosshair'
                : 'default',
      }}
      onMouseDown={handleChartMouseDown}
      onTouchStart={(e) => handleChartMouseDown(e as any)} // <--- เพิ่มตรงนี้และใช้ as any
      onMouseMove={handleChartMouseMove}
      onMouseUp={handleMouseUp}
    // onDoubleClick={handleDoubleClick}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          padding: 0,
          margin: 0,
          minHeight: 300,
        }}
      >

        <div ref={chartRef}
          onClick={handleChartClick}
          style={{ width: '100%', height: MAIN_CHART_HEIGHT }} />

        <svg
          className="absolute pointer-events-none"
          style={{
            left: 0,
            top: 0,
            zIndex: 10,
            cursor: hoveredLineIdx !== null ? 'grab' : 'default'
          }}
          width={chartSize.width}
          height={chartSize.height}
        >

          {/* Render trend lines */}
          {priceLineType === 'freestyle' && lines.map((line) => {
            if (line.tag === 'S-dash') return null;

            // ✅ 1. ตัดสินประเภทจาก "ราคาปัจจุบัน" หน้างานจริงๆ ไม่เชื่อ line.type เดิม
            const isActuallySupport = line.price <= currentPrice;

            // ✅ 2. เช็ค Visibility ตามสถานะจริงหน้างาน
            if (isActuallySupport && !showSLine) return null;
            if (!isActuallySupport && !showRLine) return null;

            const y = priceToCoordinate(line.price);
            if (y == null) return null;

            return (
              <line
                key={line.tag}
                x1={0}
                y1={y}
                x2={chartSize.width - 40}
                y2={y}
                // ✅ 3. บังคับสีตามราคาปัจจุบันเสมอ (Yellow ถ้าต่ำกว่าราคา, Purple ถ้าสูงกว่า)
                stroke={isActuallySupport ? "#fde047" : "#a78bfa"}
                strokeWidth={0.5}
                strokeOpacity={1}
              />
            );
          })}
          {/* 
           {priceLineType === 'freestyle' && resistanceTags.map((line, idx) => (
            line.tag !== 'S-dash' && (
              <line
                key={line.tag}
                x1={0}
                y1={line.startY}
                x2={chartSize.width}
                y2={line.startY}
                stroke="#fde047"
                strokeWidth={0.5}
                strokeOpacity={1}
              // ...other props...
              />
            )
          ))} */}




          {priceLineType === 'support-resistance' && srLines.map((line) => {
            if (line.type === 'support' && !showSLine) return null;
            if (line.type === 'resistance' && !showRLine) return null;
            // ใช้ priceToCoordinate(line.price) แทน line.startY
            const y = priceToCoordinate(line.price);
            if (y == null) return null;
            return (
              <line
                key={line.tag}
                x1={0}
                y1={y}
                x2={chartSize.width}
                y2={y}
                stroke={line.type === 'support' ? "#fde047" : "#a78bfa"}
                strokeWidth={1}
                strokeDasharray={2}
                strokeOpacity={1}
              />
            );
          })}

          {/* Render the line being drawn */}
          {drawingLine && (
            <line
              x1={drawingLine.startTime ? timeToCoordinate(drawingLine.startTime) : drawingLine.startX}
              y1={drawingLine.startPrice ? priceToCoordinate(drawingLine.startPrice) : drawingLine.startY}
              x2={drawingLine.endTime ? timeToCoordinate(drawingLine.endTime) : drawingLine.endX}
              y2={drawingLine.endPrice ? priceToCoordinate(drawingLine.endPrice) : drawingLine.endY}
              stroke="#fbbf24"
              strokeWidth={2}
              strokeDasharray="4"
            />
          )}

          {priceLineType === 'freestyle' && showTrendline && trendLines.map((line, idx) => {
            if (!line.startTime || !line.startPrice || !line.endTime || !line.endPrice) return null;
            const x2 = timeToCoordinate(line.endTime);
            const y2 = priceToCoordinate(line.endPrice);
            if (x2 == null || y2 == null) return null;
            return (
              <g key={line.tag}>
                {/* Invisible hitbox for selecting line */}
                <rect
                  x={Math.min(timeToCoordinate(line.startTime), x2) - 5}
                  y={Math.min(priceToCoordinate(line.startPrice), y2) - 5}
                  width={Math.abs(x2 - timeToCoordinate(line.startTime)) + 10}
                  height={Math.abs(y2 - priceToCoordinate(line.startPrice)) + 10}
                  fill="transparent"
                  stroke="transparent"
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  onClick={() => setSelectedTrendLineTag(line.tag || idx.toString())}
                />
                {/* ปุ่มลบกลมๆ */}


                {selectedTrendLineTag === (line.tag || idx.toString()) && (
                  <>
                    <circle
                      cx={x2 + 10}
                      cy={y2}
                      r={10}
                      fill="#ef4444"
                      stroke="#fff"
                      strokeWidth={2}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                      onClick={() => {
                        handleDeleteTrendLine(line.tag);
                        setSelectedTrendLineTag(null);
                      }}
                    />
                    <text
                      x={x2 + 10}
                      y={y2 + 4}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#fff"
                      style={{ pointerEvents: 'none' }}
                    >✕</text>
                  </>
                )}
              </g>
            );
          })}


          {priceLineType === 'freestyle' && showTrendline && areas.map((area, idx) => {
            if (
              area.startTime === undefined ||
              area.startPrice === undefined ||
              area.endTime === undefined ||
              area.endPrice === undefined
            ) return null;
            const x1 = timeToCoordinate(area.startTime);
            const y1 = priceToCoordinate(area.startPrice);
            const x2 = timeToCoordinate(area.endTime);
            const y2 = priceToCoordinate(area.endPrice);
            if (
              x1 === null || y1 === null ||
              x2 === null || y2 === null
            ) return null;

            // ปุ่มลบที่ขวาสุดของเส้นบน
            const deleteBtnX = Math.max(x1, x2) + 10;
            const deleteBtnY = Math.min(y1, y2);

            return (
              <g key={area.tag || idx}>
                {/* hitbox transparent สำหรับเลือก area */}
                <rect
                  x={Math.min(x1, x2)}
                  y={Math.min(y1, y2)}
                  width={Math.abs(x2 - x1)}
                  height={Math.abs(y2 - y1)}
                  fill="transparent"
                  stroke="transparent"
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  onClick={() => setSelectedAreaTag(area.tag || idx.toString())}
                />
                {/* ปุ่มลบแสดงเฉพาะ area ที่เลือก */}

                {selectedAreaTag === (area.tag || idx.toString()) && (
                  <>
                    <circle
                      cx={deleteBtnX}
                      cy={deleteBtnY}
                      r={10}
                      fill="#ef4444"
                      stroke="#fff"
                      strokeWidth={2}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                      onClick={() => {
                        setAreas(prev => prev.filter((a, i) => (a.tag || i.toString()) !== (area.tag || idx.toString())));
                        setSelectedAreaTag(null);
                      }}
                    />
                    <text
                      x={deleteBtnX}
                      y={deleteBtnY + 4}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#fff"
                      style={{ pointerEvents: 'none' }}
                    >✕</text>
                  </>
                )}
              </g>
            );
          })}


          {/* {priceLineType === 'freestyle' && showTrendline && trendLines.map((line, idx) => {
            if (!line.startTime || !line.startPrice || !line.endTime || !line.endPrice) return null;
            const x1 = timeToCoordinate(line.startTime);
            const y1 = priceToCoordinate(line.startPrice);
            const x2 = timeToCoordinate(line.endTime);
            const y2 = priceToCoordinate(line.endPrice);
            if (x1 == null || y1 == null || x2 == null || y2 == null) return null;
            return (
              <line
                key={line.tag}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#38bdf8"
                strokeWidth={2}
              />
            );
          })} */}
          {/* Render saved areas */}
          {/* {priceLineType === 'freestyle' && showTrendline && areas.map((area, idx) => {
            if (
              area.startTime === undefined ||
              area.startPrice === undefined ||
              area.endTime === undefined ||
              area.endPrice === undefined
            ) return null;
            const x1 = timeToCoordinate(area.startTime);
            const y1 = priceToCoordinate(area.startPrice);
            const x2 = timeToCoordinate(area.endTime);
            const y2 = priceToCoordinate(area.endPrice);
            if (
              x1 === null || y1 === null ||
              x2 === null || y2 === null
            ) return null;
            return (
              <rect
                key={area.tag || idx}
                x={Math.min(x1, x2)}
                y={Math.min(y1, y2)}
                width={Math.abs(x2 - x1)}
                height={Math.abs(y2 - y1)}
                fill="#60a5fa"
                fillOpacity={0.2}
                stroke="#60a5fa"
                strokeWidth={2}
              />
            );
          })} */}
          {/* Render the area being drawn */}
          {drawingArea &&
            drawingArea.startX !== undefined &&
            drawingArea.endX !== undefined &&
            drawingArea.startY !== undefined &&
            drawingArea.endY !== undefined && (
              <rect
                x={Math.min(drawingArea.startX, drawingArea.endX)}
                y={Math.min(drawingArea.startY, drawingArea.endY)}
                width={Math.abs(drawingArea.endX - drawingArea.startX)}
                height={Math.abs(drawingArea.endY - drawingArea.startY)}
                fill="#fbbf24"
                fillOpacity={0.2}
                stroke="#fbbf24"
                strokeWidth={2}
                strokeDasharray="4"
              />
            )}
        </svg>
      </div>
      {/* You can uncomment and fix the legend and tags below if needed */}
      {/* {showOverlays && (
        <div className="absolute top-4 right-4 bg-gray-800 bg-opacity-90 rounded-lg p-3 text-sm">
          ...legend and tags...
        </div>
      )} */}
      {/* {lowerIndicatorData.length > 0 && <div ref={lowerRef} />} */}
      {priceLineType === 'freestyle' && (
        <div
          className="absolute left-0 top-0 z-50 flex flex-col gap-2 p-2"
          style={{
            background: 'rgba(31, 41, 55, 0.7)', // Tailwind gray-800 with 70% opacity
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            animation: 'fadeInPanel 0.6s ease'
          }}
        >
          <style>
            {`
      @keyframes fadeInPanel {
        from { opacity: 0; transform: translateY(-10px);}
        to { opacity: 1; transform: translateY(0);}
      }
    `}
          </style>
          <button
            className={`p-2 rounded ${activeTool === 'drag' ? 'bg-green-400' : 'bg-gray-700 text-white'}`}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleToolClick('drag');
            }}
            title="Move Chart"
          >🖐</button>
          {/* <button
            className={`p-2 rounded ${activeTool === 'support' ? 'bg-[#fbbf24]' : 'bg-gray-700 text-white'}`}
            onClick={() => handleToolClick('support')}
            title="Add Support"
          >S</button>
          <button
            className={`p-2 rounded ${activeTool === 'resistance' ? 'bg-[#a78bfa]' : 'bg-gray-700 text-white'}`}
            onClick={() => handleToolClick('resistance')}
            title="Add Resistance"
          >R</button> */}
          {/* ภายในส่วนเครื่องมือ (Toolbox) */}
          <button
            className={`p-2 text-sm rounded font-bold ${activeTool === 'sr-line' ? 'bg-gradient-to-r from-[#fbbf24] to-[#a78bfa] text-black' : 'bg-gray-700 text-white'}`}
            onClick={() => setActiveTool('sr-line')}
            title="Add Support/Resistance"
          >
            S/R
          </button>
          {/* <button
            className={`p-2 rounded ${activeTool === 'line' ? 'bg-blue-400' : 'bg-gray-700 text-white'}`}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleToolClick('line');
            }}
            title="Draw Line"
          >
            <LineIcon />
          </button>
          <button
            className={`p-2 rounded ${activeTool === 'area' ? 'bg-purple-400' : 'bg-gray-700 text-white'}`}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleToolClick('area')
            }}
            title="Draw Area"
          ><SquareIcon />
          </button> */}
          <button
            className="p-2 rounded bg-red-600 text-white"
            onClick={e => {

              e.stopPropagation();
              e.preventDefault();

              handleClearAll();
              // เพิ่มฟังก์ชันหรือ state อื่นๆ ได้ เช่น รีเฟรช trendline
              if (setShowTrendline) {
                setShowTrendline(false);
                setTimeout(() => setShowTrendline(true), 10);
              }
            }}
            title="Clear All"
          >C</button>
        </div>
      )}

      {/* Custom Clear Confirmation Modal */}
      {isClearConfirmOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1c1c1e] border border-gray-800 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                ⚠️
              </div>
              <h3 className="text-xl font-bold text-white mb-2">ยืนยันการล้างกราฟ?</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                หากคุณกดยืนยัน <span className="text-red-400 font-bold">เส้นแนวรับ-ต้านทั้งหมดจะหายไป</span> รวมถึงเส้นที่นำมาจากแอดมินด้วย ไม่สามารถเรียกคืนได้
              </p>
            </div>

            <div className="flex border-t border-gray-800">
              <button
                onClick={() => setIsClearConfirmOpen(false)}
                className="flex-1 px-4 py-4 text-sm font-bold text-gray-400 hover:bg-white/5 transition-colors border-r border-gray-800"
              >
                ยกเลิก
              </button>
              <button
                onClick={confirmClearAll}
                className="flex-1 px-4 py-4 text-sm font-bold text-red-500 hover:bg-red-500/10 transition-colors"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Draggable Tags for S/R lines */}
      {priceLineType === 'freestyle' && (
        <>
          {[...supportTags, ...resistanceTags].map((tag) => {
            if (tag === 'S-dash') return null;
            const line = lines.find(l => l.tag === tag);
            if (!line) return null;

            // ✅ 1. ตัดสินประเภทจากราคาปัจจุบันเหมือนตอนวาดเส้น
            const isActuallySupport = line.price <= currentPrice;

            // ✅ 2. เช็คการแสดงผลให้ตรงกัน
            // ถ้าเป็นแนวรับ (ต่ำกว่าราคาปัจจุบัน) แต่สั่งปิด showSLine -> ไม่ต้องโชว์ปุ่ม
            if (isActuallySupport && !showSLine) return null;
            // ถ้าเป็นแนวต้าน (สูงกว่าราคาปัจจุบัน) แต่สั่งปิด showRLine -> ไม่ต้องโชว์ปุ่ม
            if (!isActuallySupport && !showRLine) return null;



            if (line.type === 'support' && !showSLine) return null;
            if (line.type === 'resistance' && !showRLine) return null;

            const boxHeight = 14;
            // เปลี่ยนตำแหน่ง top ให้ใช้ priceToCoordinate(line.price)
            const coordY = priceToCoordinate(line.price) ?? 0;
            return (
              <div
                key={tag}
                style={{
                  position: 'absolute',
                  left: 40,
                  top: coordY - boxHeight / 2,
                  zIndex: 20,
                  background: 'transparent',
                  color: 'transparent',
                  padding: 0,
                  borderRadius: 4,
                  fontWeight: 'bold',
                  cursor: isSRLocked ? 'default' : (hoveredTag === tag ? 'grab' : 'pointer'),
                  width: chartSize.width - 100,
                  height: 14,
                  boxSizing: 'border-box',
                }}
                onMouseEnter={() => setHoveredTag(tag)}
                onMouseLeave={() => setHoveredTag(null)}
                onMouseDown={() => {
                  handleTagMouseDown(tag, line.type);
                }}
                onTouchStart={(e) => handleTagMouseDown(tag, line.type, e as any)} // <--- เพิ่มตรงนี้
              >
                {/* Delete button */}
                <button
                  style={{
                    position: 'absolute',
                    left: chartSize.width - 40,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#ff007f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0 4px',
                    cursor: 'pointer',
                    fontSize: '10px',
                    zIndex: 1,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation(); // Prevent triggering parent mouse events
                    isDragging = false; // Reset dragging flag
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation(); // Prevent triggering parent touch events
                    isDragging = false; // Reset dragging flag
                  }}
                  onClick={(e) => {
                    // console.log('Delete button clicked'); // Debugging
                    e.stopPropagation();
                    handleDelete(tag); // Call the handleDelete function
                  }}
                >
                  ✕
                </button>
              </div>


            );
          })}
          {draggingTag && (
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'all' }}
              onMouseMove={handleTagMouseMove}
              onMouseUp={handleTagMouseUp}
              onTouchMove={(e) => handleTagMouseMove(e as any)} // <--- เพิ่มตรงนี้
              onTouchEnd={handleTagMouseUp} // <--- เพิ่มตรงนี้
            />
          )}
        </>
      )}
      {priceLineType === 'support-resistance' && srLines.map((line) => {
        // Only render draggable tag if the line is visible
        if (line.type === 'support' && !showSLine) return null;
        if (line.type === 'resistance' && !showRLine) return null;

        // ใช้ priceToCoordinate(line.price) แทน line.startY
        const coordY = priceToCoordinate(line.price) ?? 0;
        return (
          <div
            key={line.tag}
            style={{
              position: 'absolute',
              left: 40,
              top: coordY - 7,
              zIndex: 20,
              width: 750,
              height: 14,
              cursor: hoveredTag === line.tag ? 'grab' : 'pointer',
              userSelect: 'none',
              background: 'transparent',
              color: 'transparent',
              pointerEvents:
                (line.type === 'support' && !showSLine) ||
                  (line.type === 'resistance' && !showRLine)
                  ? 'none'
                  : 'auto',
            }}
            onMouseEnter={() => setHoveredTag(line.tag)}
            onMouseLeave={() => setHoveredTag(null)}
            onMouseDown={() => {
              // Only allow dragging if line is visible
              if (
                (line.type === 'support' && showSLine) ||
                (line.type === 'resistance' && showRLine)
              ) {
                handleSRTagMouseDown(line.tag, line.type);
              }
            }}
          />
        );
      })}
      {draggingSRTag && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'all' }}
          onMouseMove={handleSRTagMouseMove}
          onMouseUp={handleSRTagMouseUp}
        />
      )}
    </div>
  );
}

export default StockChart;

function clusterLevels(levels: { price: number; volume: number }[], threshold = 0.2) {
  // threshold: percent of price range to consider as "close"
  if (levels.length === 0) return [];
  const sorted = levels.slice().sort((a, b) => a.price - b.price);
  const clusters: { prices: number[]; volumes: number[] }[] = [];
  let current: { prices: number[]; volumes: number[] } = { prices: [sorted[0].price], volumes: [sorted[0].volume] };
  const priceRange = sorted[sorted.length - 1].price - sorted[0].price;
  const minDist = priceRange * threshold;

  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].price - current.prices[current.prices.length - 1]) < minDist) {
      current.prices.push(sorted[i].price);
      current.volumes.push(sorted[i].volume);
    } else {
      clusters.push(current);
      current = { prices: [sorted[i].price], volumes: [sorted[i].volume] };
    }
  }
  clusters.push(current);
  // Return average price of each cluster
  return clusters.map(c => ({
    price: c.prices.reduce((a, b) => a + b, 0) / c.prices.length,
    volume: c.volumes.reduce((a, b) => a + b, 0) / c.volumes.length,
  }));
}

function findSupportResistanceLevels(
  data: OHLCData[],
  tolerance = 0.1,
  minTouch = 2
): number[] {
  const levels: number[] = [];

  function isFarFromLevels(price: number, levels: number[], tolerance: number) {
    return levels.every(level => Math.abs(price - level) > tolerance);
  }

  const highs = data.map(bar => bar.h);
  const lows = data.map(bar => bar.l);

  for (let i = 2; i < data.length - 2; i++) {
    const high = highs[i];
    const low = lows[i];

    // Resistance
    if (highs[i] > highs[i - 1] && highs[i] > highs[i + 1]) {
      if (isFarFromLevels(high, levels, tolerance)) {
        levels.push(high);
      }
    }

    // Support
    if (lows[i] < lows[i - 1] && lows[i] < lows[i + 1]) {
      if (isFarFromLevels(low, levels, tolerance)) {
        levels.push(low);
      }
    }
  }

  return levels.sort((a, b) => b - a);
}

function groupLevelsIntoZones(levels: number[], zoneSize = 0.2): [number, number][] {
  if (levels.length === 0) return [];
  const zones: [number, number][] = [];
  let currentZone: number[] = [levels[0]];

  for (let i = 1; i < levels.length; i++) {
    if (Math.abs(levels[i] - currentZone[currentZone.length - 1]) <= zoneSize) {
      currentZone.push(levels[i]);
    } else {
      zones.push([Math.min(...currentZone), Math.max(...currentZone)]);
      currentZone = [levels[i]];
    }
  }
  zones.push([Math.min(...currentZone), Math.max(...currentZone)]);
  return zones;
}

function splitSupportResistance(
  zones: [number, number][],
  currentPrice: number
): { supports: [number, number][], resistances: [number, number][] } {
  const supports: [number, number][] = [];
  const resistances: [number, number][] = [];
  for (const [low, high] of zones) {
    if (high < currentPrice) {
      supports.push([low, high]);
    } else if (low > currentPrice) {
      resistances.push([low, high]);
    }
  }
  return { supports, resistances };
}

function findStrictDowntrendLine(data: OHLCData[]) {
  let best = null;
  for (let i = 0; i < data.length - 1; i++) {
    for (let j = data.length - 1; j > i; j--) {
      const x1 = i, y1 = data[i].h;
      const x2 = j, y2 = data[j].h;
      if (y2 >= y1) continue; // Must be a downtrend
      let valid = true;
      for (let k = i + 1; k < j; k++) {
        const yk = y1 + ((y2 - y1) * (k - x1)) / (x2 - x1);
        if (data[k].h > yk + 1e-8) {
          valid = false;
          break;
        }
      }
      if (valid) {
        if (!best || j - i > best.j - best.i) {
          best = { i, j, x1, y1, x2, y2 };
        }
      }
    }
  }
  if (!best) {
    // fallback: use max high and last candle
    let maxIdx = 0;
    for (let i = 1; i < data.length; i++) if (data[i].h > data[maxIdx].h) maxIdx = i;
    const slope = (data[data.length - 1].h - data[maxIdx].h) / (data.length - 1 - maxIdx);
    const startValue = data[0].h; // Use first candle high
    const endValue = data[0].h + slope * (data.length - 1); // extend to last candle
    return {
      start: { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: startValue },
      end: { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: endValue }
    };
  }
  // Calculate slope and extend to start and end
  const { x1, y1, x2, y2 } = best;
  const slope = (y2 - y1) / (x2 - x1);
  const startValue = y1 - slope * x1; // value at x=0
  const endValue = y1 + slope * (data.length - 1 - x1); // value at last index
  return {
    start: { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: startValue },
    end: { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: endValue }
  };
}

function findStrictUptrendLine(data: OHLCData[]) {
  let best = null;
  for (let i = 0; i < data.length - 1; i++) {
    for (let j = data.length - 1; j > i; j--) {
      const x1 = i, y1 = data[i].l;
      const x2 = j, y2 = data[j].l;
      if (y2 <= y1) continue; // Must be an uptrend
      let valid = true;
      for (let k = i + 1; k < j; k++) {
        const yk = y1 + ((y2 - y1) * (k - x1)) / (x2 - x1);
        if (data[k].l < yk - 1e-8) {
          valid = false;
          break;
        }
      }
      if (valid) {
        if (!best || j - i > best.j - best.i) {
          best = { i, j, x1, y1, x2, y2 };
        }
      }
    }
  }
  if (!best) {
    // fallback: use min low and last candle
    let minIdx = 0;
    for (let i = 1; i < data.length; i++) if (data[i].l < data[minIdx].l) minIdx = i;
    const slope = (data[data.length - 1].l - data[minIdx].l) / (data.length - 1 - minIdx);
    const startValue = data[0].l; // Use first candle low
    const endValue = data[0].l + slope * (data.length - 1); // extend to last candle
    return {
      start: { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: startValue },
      end: { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: endValue }
    };
  }
  // Calculate slope and extend to start and end
  const { x1, y1, x2, y2 } = best;
  const slope = (y2 - y1) / (x2 - x1);
  const startValue = y1 - slope * x1; // value at x=0
  const endValue = y1 + slope * (data.length - 1 - x1); // value at last index
  return {
    start: { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: startValue },
    end: { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: endValue }
  };
}
// Helper: Find top N popular price bins (by candle touches)
function findPopularPriceLevels(data: OHLCData[], step = 0.1, topN = 3) {
  if (!data.length) return [];
  const priceCount: Record<string, number> = {};
  let minPrice = data[0].l, maxPrice = data[0].h;
  data.forEach(bar => {
    minPrice = Math.min(minPrice, bar.l);
    maxPrice = Math.max(maxPrice, bar.h);
  });
  for (let price = minPrice; price <= maxPrice; price += step) {
    priceCount[price.toFixed(2)] = 0;
  }
  data.forEach(bar => {
    [bar.o, bar.h, bar.l, bar.c].forEach(val => {
      const key = (Math.round(val / step) * step).toFixed(2);
      if (priceCount[key] !== undefined) priceCount[key]++;
    });
  });
  // Sort bins by count, descending
  const sorted = Object.entries(priceCount)
    .sort((a, b) => b[1] - a[1])
    .map(([price, count]) => ({ price: parseFloat(price), count }));
  return sorted.slice(0, topN);
}

function linearRegressionTrendLine(
  data: OHLCData[],
  type: 'high' | 'low'
): { start: { time: UTCTimestamp; value: number }, end: { time: UTCTimestamp; value: number } } | null {
  if (data.length < 2) return null;
  // x: index, y: price
  const xs = data.map((_, i) => i);
  const ys = data.map(bar => type === 'high' ? bar.h : bar.l);

  // Calculate means
  const xMean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const yMean = ys.reduce((a, b) => a + b, 0) / ys.length;

  // Calculate slope (m) and intercept (b)
  let num = 0, den = 0;
  for (let i = 0; i < xs.length; i++) {
    num += (xs[i] - xMean) * (ys[i] - yMean);
    den += (xs[i] - xMean) ** 2;
  }
  const m = den === 0 ? 0 : num / den;
  const b = yMean - m * xMean;

  // Calculate start and end points
  const start = { time: Math.floor(data[0].t / 1000) as UTCTimestamp, value: m * 0 + b };
  const end = { time: Math.floor(data[data.length - 1].t / 1000) as UTCTimestamp, value: m * (xs.length - 1) + b };

  return { start, end };
}
